<script>
	import { onMount, createEventDispatcher } from 'svelte';
	import Identity from 'svelte-key';
	import { fly, fade } from 'svelte/transition';
	import mapboxgl from 'maplibre-gl';
	import MapboxDraw from '@mapbox/mapbox-gl-draw';
	import * as turf from '@turf/turf';
	import bbox from '@turf/bbox';
	import * as helpers from '@turf/helpers';
	import { addMessages, getServer, getLocalServer, generateID, cachedFetch, fetchData, fetchSnapshot, hasCache, getCache, setCache, loadCacheIndex, loadMessages, loadHistory, getHistory, addHistory, editHistory, clearHistory, loadAccount, layerChanged, isTouchEnabled, getCenter, openIfExists, match } from '../components/shared/utils/Data.svelte'
	import { compass, today, attach, swap, analytics, squareOutline, radioButtonOn, image, navigate, closeCircleOutline, eyeOff, trash, create, share, brush, move, colorWand, checkmark, sync, podium, locate } from 'ionicons/icons/index.mjs';
	import LoginModal from '../components/shared/modal/Login.svelte';
	import ErrorsModal from '../components/shared/modal/Errors.svelte';
	import ConfirmModal from '../components/shared/modal/Confirm.svelte';
	import OptionModal from '../components/shared/modal/Option.svelte';
	import MediaViewModal from '../components/shared/modal/MediaView.svelte';
	import MediaUploadModal from '../components/shared/modal/MediaUpload.svelte';
	import HistoryModal from '../components/shared/modal/History.svelte';
	import ForbiddenModal from '../components/shared/modal/Forbidden.svelte';
	import FormModal from '../components/shared/modal/Form.svelte';
	import FormsModal from '../components/shared/modal/Forms.svelte';
	import PassphraseModal from '../components/shared/modal/Passphrase.svelte';
	import SettingsModal from '../components/shared/modal/Settings.svelte';
	import GridModal from '../components/shared/grid/Source.svelte';
	import ProfileModal from '../components/shared/modal/Profile.svelte';
	import Tree from '../components/map/Sidebar.svelte';
	import Callout from '../components/map/callout/Callout.svelte';
	import MessageModal from '../components/shared/modal/Message.svelte';
	import MessageViewModal from '../components/shared/modal/MessageView.svelte';
	import InfoModal from '../components/shared/modal/Info.svelte';
	import {
		SnapPolygonMode,
		SnapPointMode,
		SnapLineMode,
		SnapModeDrawStyles,
	} from "mapbox-gl-draw-snap-mode";
	import "maplibre-gl/dist/maplibre-gl.css";

	const dispatch = createEventDispatcher();

	export let vitals;
	export let page;
	export let messageToken;
	
	let locationTimer = null;
	let socket = null;
	let toasts = [];
	let snapIds = [];
	let snapFeatures = [];
	let locationTimeout = null;
	let pending = {};
	let firstLocation = true;
	let polling = false;
	let pendingSources = 0;
	let sourcesLoaded = false;
	let bearing = 0;
	let keys = {
		users: "1",
		workspaces: "1",
		content: "1",
		toast: "1",
		layers: "1",
		feature: "1"
	}
	let units = {
		feet: "ft",
		meters: "m",
		miles: "mi",
		kilometers: "km"
	}
	let isOpen = {
		sidebar: true,
		callout: false
	}
	let selected = {
		index: 0,
		features: [],
		layer: null,
		feature: null,
		label: "",
		idField: ""
	}
	let isPublic = false;
	let vw = 0;
	let vh = 0;

	let lastDraw = null;
	let lastPoll = (new Date(Date.now())).getTime();
	let tab = "layers";
	let details = "details";
	let location = {
		status: "",
		enabled: false,
		label: "off"
	};
	let userLayer = null;
	let accuracyControl = null;
	let navControl = null;
	let geolocateControl = null;
	let drawControl = null;
	let groups = {};
	let workspace = "";
	let projects = {};
	let mapId = "";
	let map = null;
	let zoom = 10;
	let mapLoaded = false;
	let isMeasuring = false;
	let isDrawing = false;
	let t0 = null;
	let interactiveLayers = [];
	let layers = {};
	let links = {};
	let cloudLayers = {};
	let liveLayers = {};
	let config = {};
	let history = [];
	let messages = [];
	let forms = [];
	let tooltips = false;
	let drawTools = false;
	let snapshotList = {};
	let editFeature = null;
	let lineOrigin = null;
	let locationLineInt = null;
	let xmlLocation = false;
	let handlers = {};
	let measure = {
		click: null,
		move: null,
		start: null,
		end: null
	}
	let measurements = {
		"type": "FeatureCollection",
		"features": []
	};
	let linestring = {
		"type": "Feature",
		"properties": {
			"_id": "user"
		},
		"geometry": {
			"type": "LineString",
			"coordinates": []
		}
	};
	let modal = {
		view: ""
	};
	let session = {
        workspaces: []
	};
	if (localStorage.getItem("session")) {
		session = JSON.parse(localStorage.getItem("session"));
	}
	let modalViews = {
		confirm: ConfirmModal,
		option: OptionModal,
		login: LoginModal,
		mediaView: MediaViewModal,
		history: HistoryModal,
		mediaUpload: MediaUploadModal,
		forbidden: ForbiddenModal,
		form: FormModal,
		forms: FormsModal,
		grid: GridModal,
		passphrase: PassphraseModal,
		settings: SettingsModal,
		profile: ProfileModal,
		errors: ErrorsModal,
		message: MessageModal,
		messageView: MessageViewModal,
		info: InfoModal,
	}
	let settings = {
		location: "default",
		units: "feet",
		tracking: false,
		offset: false,
		bearing: 0,
		distance: 0,
		hasMaxAccuracy: false,
		maxAccuracy: 10,
		rotation: false,
		newTab: false,
		serial: "",
		wifiOnly: false,
		interval: "1"
	}

	onMount(async () => {
		vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
		vh = Math.max(document.documentElement.clientHeight, window.innerHeight || 0) * 0.01;
		if (vw < 800) {
			isOpen.sidebar = false;
		}
		if (!isTouchEnabled) {
			tooltips = false;
		}
		document.documentElement.style.setProperty('--vh', `${vh}px`);
		window.addEventListener("resize", function() {
			vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
			vh = Math.max(document.documentElement.clientHeight, window.innerHeight || 0) * 0.01;
			document.documentElement.style.setProperty('--vh', `${vh}px`);
		})
		mapId = window.location.pathname.substring(window.location.pathname.lastIndexOf("/") + 1);
		if (!mapId)
			mapId = "5cbe0b44-f890-487d-8e0d-68b7b90f3d59";

		var settingsString = localStorage.getItem("settings");
		if (settingsString) {
			settings = JSON.parse(settingsString);
		}
		if (!settings.wifiOnly) {
			settings.wifiOnly = false;
		}
		poll();

		handlers["app:location"] = gotLocation;
		handlers["app:location:xml"] = hasLocation;
    if (window.chrome && window.chrome.webview) {
      window.chrome.webview.addEventListener("message", messageRouter);
    } else {
      window.addEventListener("message", messageHandler);
    }
		if (window.platform == "Windows" && window.apiVersion >= 4) {
			window.chrome.webview.postMessage(JSON.stringify({
					type: "app:location:xml",
			}))
		}

		let params = (new URL(document.location)).searchParams;
		if (params.get("public") !== null)
			isPublic = true;
		if (isPublic) {
			let [res, err] = await fetchData("/v1/session/map/" + mapId);
			if (err) {
				modal = {
					view: "forbidden"
				};
				return;
			}
			if (res.data) {
				session = res.data;
				if (window.platform == "Android" && window.apiVersion >= 4) {
					androidAdapter.initBrain(session.account, session.query, settings.wifiOnly ? "1" : "0");
				}
				else if (window.platform == "iOS" && window.apiVersion >= 2) {
					try {
							webkit.messageHandlers.mediaHandler.postMessage({
									type: "init",
									account: session.account,
									query: session.query,
									wifi: settings.wifiOnly ? "1" : "0"
							});
					} catch(err) {
							console.error(err);
							createToast({
									message: "iOS init error."
							});
					}
				}
				polling = true;
				getMap();
			}
			else {
				modal = {
					view: "passphrase"
				};
			}
		}
		else if (session.token) {
			if (window.platform == "Android" && window.apiVersion >= 4) {
				androidAdapter.initBrain(session.account, session.query, settings.wifiOnly ? "1" : "0");
			}
			else if (window.platform == "iOS" && window.apiVersion >= 2) {
				try {
						webkit.messageHandlers.mediaHandler.postMessage({
								type: "init",
								account: session.account,
								query: session.query,
								wifi: settings.wifiOnly ? "1" : "0"
						});
				} catch(err) {
						console.error(err);
						createToast({
								message: "iOS init error."
						});
				}
			}
			polling = true;
			getMap();
			let [res, err] = await fetchData("/v1/session", {
				headers: {
					"Authorization": "Bearer " + session.token,
					"x-message-token": messageToken
				}
			});
			if (err == "invalid or expired jwt") {
				emailAuth()
			}
			else if (!err) {
				session = res.data;
				localStorage.setItem("session", JSON.stringify(session));
			}
		}
		else {
			emailAuth();
		}
  });
	function messageRouter(event) {
    event = event.data || event.detail;
    if (event.status == "error") {
      console.log(event.data);
      return;
    }
    if (handlers[event.type]) {
      handlers[event.type](event.data);
    }
  }
	async function emailAuth() {
		let [res, err] = await fetchData("/v1/session/email");
		if (!err) {
			session = res.data;
			localStorage.setItem("session", JSON.stringify(session));
			if (window.platform == "Android" && window.apiVersion >= 4) {
				androidAdapter.initBrain(session.account, session.query, settings.wifiOnly ? "1" : "0");
			}
			else if (window.platform == "iOS" && window.apiVersion >= 2) {
				try {
						webkit.messageHandlers.mediaHandler.postMessage({
								type: "init",
								account: session.account,
								query: session.query,
								wifi: settings.wifiOnly ? "1" : "0"
						});
				} catch(err) {
						console.error(err);
						createToast({
								message: "iOS init error."
						});
				}
			}
			polling = true;
			getMap();
		}
		else {
			modal = {
				view: "login"
			};
		}
	}
	async function messageHandler(message) {
		if (message.detail.type == "modal") {
			modal = message.detail.data;
		}
		else if (message.detail.type == "draw") {
			let data = message.detail.data;
			map.off("click", measure.click);
			map.off("mousemove", measure.move);
			map.off("touchstart", measure.start);
			map.off("touchend", measure.end);
			drawControl.delete(snapIds);
			if (data.type) {
				snapFeatures.map(function (feature) {
					var ids = drawControl.add(feature);
					snapIds = snapIds.concat(ids);
				})
			}
			drawHandler(data.type);
		}
		else if (message.detail.type == "location-line") {
			if (!locationLineInt) {
				var interval = Number(settings.interval) * 1000;
				lineOrigin = [location.longitude, location.latitude]
				locationLineInt = setInterval(function() {
					var feature = drawControl.get("location-line");
					if (!feature && !lineOrigin) {
						lineOrigin = [location.longitude, location.latitude]
						return;
					}

					if (!feature) {
						feature = {
							id: 'location-line',
							type: 'Feature',
							properties: {},
							geometry: { type: 'LineString', coordinates: [lineOrigin,[location.longitude, location.latitude]] }
						};
						drawControl.add(feature);
						drawControl.changeMode("simple_select", { featureIds: ["location-line"] });
						return;
					}

					
					feature.geometry.coordinates.push([location.longitude, location.latitude]);
					drawControl.delete("location-line");
					drawControl.add(feature);
					drawControl.changeMode("simple_select", { featureIds: ["location-line"] });
				}, interval);
			}
			else {
				clearInterval(locationLineInt);
				lineOrigin = null;
				locationLineInt = null;
			}
		}
		else if (message.detail.type == "geometry") {
			let data = message.detail.data;
			if (data.type == "edit") {
				map.off("click", measure.click);
				map.off("mousemove", measure.move);
				map.off("touchstart", measure.start);
				map.off("touchend", measure.end);
				editGeomHandler();
			}
			else if (data.type == "location") {
				editGeomLocationHandler();
			}
		}
		else if (message.detail.type == "clear") {
			let data = message.detail.data;
			if (data && data.type && data.type == "snaps") {
				drawControl.delete(snapIds);
				snapFeatures = [];
				snapIds = [];
			}
			else {
				drawControl.deleteAll();
				snapFeatures.map(function (feature) {
					var ids = drawControl.add(feature);
					snapIds = snapIds.concat(ids);
				})
			}
		}
		else if (message.detail.type == "measure") {
			//map.off("click", measure.click);
			//map.off("mousemove", measure.move);
			//map.off("touchstart", measure.start);
			//map.off("touchend", measure.end);
			measureHandler();
		}
		else if (message.detail.type == "delete") {
			deleteHandler();
		}
		else if (message.detail.type == "context") {
			let data = message.detail.data;
			contextHandler(data.lat, data.lng, userLayer);
		}
		else if (message.detail.type == "public") {
			session = message.detail.data;
			getMap();
		}
		else if (message.detail.type == "settings") {
			settings = message.detail.data;
			if (settings.rotation) {
				map.dragRotate.enable();
    		map.touchZoomRotate.enableRotation();
			}
			else {
				map.dragRotate.disable();
    		map.touchZoomRotate.disableRotation();
			}
			localStorage.setItem("settings", JSON.stringify(settings));
		}
		else if (message.detail.type == "goto") {
			var data = message.detail.data;
			var layer = data.layer;
			if (data.layer.id == "reportlog") {
				if (data.feature.geometry.type == "LineString")
					layer = { id: "reportlog_line" };
				else if (data.feature.geometry.type == "Polygon")
					layer = { id: "reportlog_fill" };
			}
			map.flyTo({center: [data.feature.metadata.longitude, data.feature.metadata.latitude], zoom: 18});
    		selectFeature(data.feature, layer, data.label, data.idField);
		}
		else if (message.detail.type == "history") {
			var data = message.detail.data;
			if (data.action == "add") {
				history = addHistory(data.report);
			}
			else if (data.action == "edit") {
				history = editHistory(data.report);
			}
			deselectFeature();
      map.getSource("history").setData({
        type: "FeatureCollection",
        features: history
      });
		}
		else if (message.detail.type == "messages") {
			var data = message.detail.data;
			messages = data.concat(messages);
			addMessages(data);
		}
		else if (message.detail.type == "clearlog") {
			history = [];
			clearHistory();			
      map.getSource("history").setData({
        type: "FeatureCollection",
        features: history
      });
		}
		else if (message.detail.type == "pending") {
			pending = message.detail.data;
		}
		else if (message.detail.type == "tab") {
			tab = message.detail.data;
		}
		else if (message.detail.type == "details") {
			details = message.detail.data;
		}
		else if (message.detail.type == "session") {
			session = message.detail.data;
			if (!session.token) {
				localStorage.removeItem("session");
				await fetchData("/v1/session/end", {
					headers: {
						"Authorization": "Bearer " + session.token,
						"x-message-token": messageToken
					}
				});
				polling = false;
				dispatch("message", {
					type: "poll",
					data: null
				})
				window.location = "/";
			}
			else {
				localStorage.setItem("session", JSON.stringify(session));
				if (window.platform == "Android" && window.apiVersion >= 4) {
					androidAdapter.initBrain(session.account, session.query, settings.wifiOnly ? "1" : "0");
				}
				else if (window.platform == "iOS" && window.apiVersion >= 2) {
					try {
							webkit.messageHandlers.mediaHandler.postMessage({
									type: "init",
									account: session.account,
									query: session.query,
									wifi: settings.wifiOnly ? "1" : "0"
							});
					} catch(err) {
							console.error(err);
							createToast({
									message: "iOS init error."
							});
					}
				}
				polling = true;
				getMap();
			}
		}
		else if (message.detail.type == "select-index") {
			selected.index = message.detail.data;
			var feature = selected.features[selected.index];
			var layer = feature.layer;

			if (layer.id == "__user")
				userClickHandler(feature, layer, "User Feature", "_id")
			else if (layer.id == "reportlog" || layer.id == "reportlog_line" || layer.id == "reportlog_fill")
				selectFeature(feature, layer, "Log Feature", "history_id")
			else if (layer.metadata && layer.metadata["geosync:live"]) {
				selectFeature(feature, layer, feature.properties[feature.layer.metadata["geosync:label"]], layer.metadata["geosync:id"])
			}
			else {
				//selectFeature(feature, layer, feature.properties[feature.layer.metadata["geosync:label"]], layer.metadata["geosync:id"])
				featureLookup(feature, layer, feature.properties[feature.layer.metadata["geosync:label"]], layer.metadata["geosync:id"])
			}
		}
		else if (message.detail.type == "select") {
			var data = message.detail.data;
			deselectFeature();
			selectFeature(data.feature, data.layer, data.label, data.idField);
		}
		else if (message.detail.type == "grid") {
			var data = message.detail.data;
			if (data.layer && data.feature)
				zoomTo(data.layer, data.feature);
		}
		else if (message.detail.type == "deselect") {
			clearUserLayer(null);
		}
		else if (message.detail.type == "toast") {
			createToast(message.detail.data);
		}
		else if (message.detail.type == "reloadall") {
			console.log("reloadall");
			for(let [key, val] of Object.entries(map.getStyle().sources)) {
				if (key.startsWith("__") || key == "geolocation")
					return;
				if (val.type === "geojson") {
					if (cloudLayers[key]) {
						updateSource(key);
					}
					else if (typeof val.data === "object" && !liveLayers[key]) {
						if (key != "empty")
							updateLayer(key);
					}
				}
			}
			if (pendingSources == 0)
						sourcesComplete();
		}
		else if (message.detail.type == "clear") {
			closeToast(message.detail.data.id);
		}
		else if (message.detail.type == "media") {
			if (message.detail.data.isScene) {
				let [res, err]  = await fetchData("/v1/media/project/" + config.project + "/" + config.media_id, {
						headers: {
								'Authorization': 'Bearer ' + session.token
						}
				});
				if (err) {
						console.log(err);
						return;
				}

				config.media = res.data.media;
				keys.tree = generateID();
			}
			else {
				selectFeature(selected.feature, selected.layer, selected.label, selected.idField);
			}
		}
	}
	function initMap(res) {
		config = res.data;
		console.log(config);
		console.log(config.role > 1);
			cloudLayers = {};
			liveLayers = {};

			for(let [key, val] of Object.entries(config.style.sources)) {
				if (val.type === "geojson" && typeof val.data === "string" && (val.data == "" || val.data == "live")) {
					val.data = {
						type: "FeatureCollection",
						features: []
					}
				}
				else if (val.type === "geojson" && val.attribution == "source") {
					cloudLayers[key] = true;
				}
				else if (val.type === "geojson" && val.attribution == "dynamic") {
					liveLayers[key] = true;
				}
			}
			
			groups = {};
			groups["__default__"] = [];
			config.style.layers.map(function (layer) {
				if (layer.metadata && layer.metadata["geosync:group"]) {
					if (!groups[layer.metadata["geosync:group"]])
						groups[layer.metadata["geosync:group"]] = [];
					groups[layer.metadata["geosync:group"]].push(layer);
				}
				else if (layer.id.includes("-")) {
					var prefix = layer.id.split("-")[0];
					if (!groups[prefix])
						groups[prefix] = [];
					groups[prefix].push(layer);
				}
				else {
					groups[layer.id] = [layer];
				}
			})
			
			if (config.role >= 2) {
				loadHistory(mapId, function (result) {
						history = result;
				});
			}

			if (config.forms != "") {
				let formList = config.forms.split(",");
				formList.map(function (formId) {
					if (hasCache("form", formId)) {
						getCache("form", formId, function (formData) {
							var index = forms.findIndex(x => x.id == formId);
							if (index == -1)
								forms.push(formData.data);
							else
								forms[index] = formData.data;
						})
					}
					console.log(session.token);
					cachedFetch("/v1/forms/" + formId, "form", session.id, formId, {
						headers: {
							'Authorization': 'Bearer ' + session.token
						}
					}, async function ([res, err]) {
						if (err) {
							console.log(err);
						}
						else if (res) {
							console.log(res);
							var index = forms.findIndex(x => x.id == formId);
							if (index == -1)
								forms.push(res.data);
							else
								forms[index] = res.data;
						}
					})
				})
			}
			createMap(cloudLayers, liveLayers);
	}
	function getMap() {
		loadAccount(session.account + "_" + session.id, function () {
				dispatch("message", {
					type: "poll",
					data: session.token
				})
		})
		loadCacheIndex(session.account, function () {
			if (hasCache("map", session.id + "_" + mapId)) {
				getCache("map", session.id + "_" + mapId, function (mapData) {
					initMap(mapData);
					cachedFetch("/v1/maps/" + mapId, "map", session.id, mapId, {
						headers: {
								'Authorization': 'Bearer ' + session.token
						}
					}, function ([res, err]) {
						if (!err) {
							config.media = res.data.media;
						}
					});
				})
			}
			else {
				cachedFetch("/v1/maps/" + mapId, "map", session.id, mapId, {
								headers: {
										'Authorization': 'Bearer ' + session.token
								}
						}, async function ([res, err]) {
					if (err) {
						console.log(err);
						return;
					}
					else if (res) {
						initMap(res);
					}
				});
			}
		})

	}
	function updateLocation(locationData) {
		if (!location.enabled)
				return false;
			var accuracy = 100;
			if (locationData.accuracy) {
				var accuracyMeters = Number(locationData.accuracy);
				var accuracyFeet = (accuracyMeters * 3.28084).toFixed(2);
				accuracy = accuracyFeet;
			}
			else if (locationData.horAccuracy) {
				var accuracyMeters = Number(locationData.horAccuracy);
				var accuracyFeet = (accuracyMeters * 3.28084).toFixed(2);
				accuracy = accuracyFeet;
			}
			location = locationData;
			location.accuracy = accuracy;
			location.status = "online";
			location.enabled = true;
			location.label = "active";
			if (location.latitude)
				location.latitude = Number(location.latitude);
			if (location.longitude)
				location.longitude = Number(location.longitude);
			location.timestampRaw = location.timestamp;
			if (location.timestamp)
				location.timestamp = new Date(Number(location.timestamp)).toISOString();
			else {
				location.timestamp = new Date().toISOString();
			}
			if (location.latitude && location.longitude) {
				if (settings.offset) {
					location.origin = {
						latitude: location.latitude,
						longitude: location.longitude
					}
					var bearing = settings.bearing;
					if (bearing > 180) {
						bearing = bearing - 360;
					} 
					var offset = turf.default.destination([location.longitude, location.latitude], settings.distance, bearing, {units: settings.units});
					console.log(offset);
					location.longitude = offset.geometry.coordinates[0];
					location.latitude = offset.geometry.coordinates[1];
				}
				map.getSource("geolocation").setData({
					type: "FeatureCollection",
					features: [{
						type: "Feature",
						properties: location,
						geometry: {
							type: "Point",
							coordinates: [location.longitude, location.latitude]
						}
					}]
				});
				map.getSource("navigation").setData({
					type: "FeatureCollection",
					features: []
				});
				map.getSource("origin").setData({
					type: "FeatureCollection",
					features: []
				});
				map.getSource("offset").setData({
					type: "FeatureCollection",
					features: []
				});
				if (navControl)
					map.removeControl(navControl);
				if (selected.feature) {
					var center = getCenter(selected.feature)
					var midpoint = [(location.longitude + center[0]) / 2, (location.latitude + center[1]) / 2];
					var lineString = {
						type: "Feature",
						properties: location,
						geometry: {
							type: "LineString",
							coordinates: [[location.longitude, location.latitude], center]
						}
					}
					const distance = turf.default.length(lineString, {units: settings.units});
					var rounded_length = Math.round(distance*100)/100;

					map.getSource("navigation").setData({
						type: "FeatureCollection",
						features: [lineString]
					});
					navControl = new LabelButton('<i class="icon icon-resize-horiz"></i> ' + rounded_length.toLocaleString() + " " + units[settings.units])
					map.addControl(navControl, "top-left");
				}
				if (accuracyControl)
					map.removeControl(accuracyControl);
				let icon = '<i class="icon icon-location"></i> ';
				if (settings.offset) {
					icon = '<i class="icon icon-copy"></i> ';
				}
				accuracyControl = new LabelButton(icon + location.accuracy + " ft")
				map.addControl(accuracyControl, "top-left");
				if (firstLocation) {
					map.flyTo({center: [location.longitude, location.latitude], zoom: 18});
					firstLocation = false;
				}
				else if (settings.tracking) {
					map.panTo([location.longitude, location.latitude]);
				}
				if (settings.offset) {
					map.getSource("origin").setData({
						type: "FeatureCollection",
						features: [{
							type: "Feature",
							properties: location,
							geometry: {
								type: "Point",
								coordinates: [location.origin.longitude, location.origin.latitude]
							}
						}]
					});
					var lineString = {
						type: "Feature",
						properties: location,
						geometry: {
							type: "LineString",
							coordinates: [[location.longitude, location.latitude], [location.origin.longitude, location.origin.latitude]]
						}
					}
					map.getSource("offset").setData({
						type: "FeatureCollection",
						features: [lineString]
					});
				}
			}
			if (locationTimeout)
				clearTimeout(locationTimeout);
			locationTimeout = setTimeout(function () {
				if (location && location.enabled) {
					location.status = "away";
					location.label = "stale";
				}
			}, 60000);
	}
	window.updateLocation = updateLocation;
	async function createMap(cloudLayers, liveLayers) {
		if (config.style.glyphs.startsWith("/"))
			config.style.glyphs = getServer() + config.style.glyphs;
		if (window.platform == "iOS") {
			config.style.sprite = "";
		}
		else {
			if (config.style.sprite.startsWith("/"))
				config.style.sprite = getServer() + config.style.sprite;
		}
		

		config.style.sources["empty"] = {
			"type": "geojson",
			"data": {
				"type": "FeatureCollection",
				"features": []
			}
		}
		config.style.sources["empty_raster"] = {
			"type": "raster",
			"tiles": ["https://geosync.blob.core.windows.net/tilesets/empty/{z}/{y}/{x}"],
			"bounds": [0, 0, 0, 0]
		}
		config.style.sources["empty_vector"] =  {
			"type": "vector",
			"tiles": ["https://geosync.blob.core.windows.net/tilesets/empty/{z}/{y}/{x}"],
			"bounds": [0, 0, 0, 0]
		}
		config.style.sources["empty_dem"] =  {
			"type": "raster-dem",
			"tiles": ["https://geosync.blob.core.windows.net/tilesets/empty/{z}/{y}/{x}"],
			"bounds": [0, 0, 0, 0]
		}
		config.style.sources["empty_image"] = {
			"type": "image",
			"coordinates": [
				[0, 0],
				[0, 0],
				[0, 0],
				[0, 0]
			],
			"url": "https://geosync.blob.core.windows.net/empty.png",
		}

		if (map)
			map.remove();
			map = new mapboxgl.Map({
						container: "map",
						minZoom: Number(config.min_zoom),
						maxZoom: Number(config.max_zoom),
            style: config.style,
            attributionControl: false,
			pitchWithRotate: false,
			transformRequest: (url, resourceType) => {
				var inner = document.querySelector(".maplibregl-ctrl-attrib-inner");
				if (inner)
					inner.textContent = inner.textContent.replaceAll("source", "").replaceAll("dynamic", "").replaceAll(" | ", "");
				console.log(url)
				console.log(resourceType)
				if (url.startsWith("https://geosync.blob.core.windows.net/empty") && (resourceType == "Tile" || resourceType == "Image")) {
					return {
						url: url
					}
				}
				else if (url.startsWith("https://geosync.blob.core.windows.net/tilesets") && (resourceType == "Tile" || resourceType == "Image")) {
					return {
						url: url
					}
				}
				else if (url.startsWith("https://geosync.blob.core.windows.net/ky3in") && (resourceType == "Tile" || resourceType == "Image")) {
					return {
						url: url
					}
				}
				else if (url.startsWith("https://geosync.blob.core.windows.net") && (resourceType == "Tile" || resourceType == "Image")) {
					return {
						url: url + "?" + session.query
					}
				}
				else if (url == "https://maps.tilehosting.com/data/v3.json?key=02rAug6816zxBfqKVHYR" && resourceType == "Source") {
					return {
						url: "https://api.maptiler.com/tiles/v3/tiles.json?key=02rAug6816zxBfqKVHYR"
					}
				}
				else if (url.startsWith("/") && (resourceType == "Source" || resourceType == "Tile")) {
					return {
						url: getServer() + url,
						headers: { "Authorization": "Bearer " + session.token}
					}
				}
				else if (url.startsWith(getServer()) && resourceType == "Source") {
					return {
						headers: { "Authorization": "Bearer " + session.token}
					}
				}
				else if (url.endsWith("sprite@2x.png") || url.endsWith("sprite.png")) {
					setTimeout(function() {
						return {
							url: url
						}
					}, 1000);
				}
			}
		});
		
		if (settings.rotation) {
			map.dragRotate.enable();
    	map.touchZoomRotate.enableRotation();
		}
		else {
			map.dragRotate.disable();
    	map.touchZoomRotate.disableRotation();
		}

		geolocateControl = new mapboxgl.GeolocateControl({
			positionOptions: {
				enableHighAccuracy: true
			},
			showUserLocation: false,
			trackUserLocation: true
		});
		
		map.addControl(geolocateControl, "top-right");
		geolocateControl.on("geolocate", function (e) {
			updateLocation({
				accuracy: e.coords.accuracy,
				altitude: e.coords.altitude,
				altitudeAccuracy: e.coords.altitudeAccuracy,
				heading: e.coords.heading,
				latitude: e.coords.latitude,
				longitude: e.coords.longitude,
				speed: e.coords.speed,
				timestamp: e.timestamp
			})
		});
		geolocateControl.on("error", function (e) {
			location.status = "busy";
			location.label = "error";
		});
		map.addControl(new mapboxgl.ScaleControl());
		
		drawControl = new MapboxDraw({
			displayControlsDefault: false,
			modes: {
				...MapboxDraw.modes,
				draw_point: SnapPointMode,
				draw_polygon: SnapPolygonMode,
				draw_line_string: SnapLineMode,
			},
			styles: [
				{
					'id': 'gl-draw-polygon-fill-inactive',
					'type': 'fill',
					'filter': ['all',
					['==', 'active', 'false'],
					['==', '$type', 'Polygon'],
					['!=', 'mode', 'static']
					],
					'paint': {
					'fill-color': '#3bb2d0',
					'fill-outline-color': '#3bb2d0',
					'fill-opacity': 0.1
					}
				},
				{
					'id': 'gl-draw-polygon-fill-active',
					'type': 'fill',
					'filter': ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
					'paint': {
					'fill-color': '#00ab5f',
					'fill-outline-color': '#00ab5f',
					'fill-opacity': 0.1
					}
				},
				{
					'id': 'gl-draw-polygon-midpoint',
					'type': 'circle',
					'filter': ['all',
					['==', '$type', 'Point'],
					['==', 'meta', 'midpoint']],
					'paint': {
					'circle-radius': 3,
					'circle-color': '#00ab5f'
					}
				},
				{
					'id': 'gl-draw-polygon-stroke-inactive',
					'type': 'line',
					'filter': ['all',
					['==', 'active', 'false'],
					['==', '$type', 'Polygon'],
					['!=', 'mode', 'static']
					],
					'layout': {
					'line-cap': 'round',
					'line-join': 'round'
					},
					'paint': {
					'line-color': '#3bb2d0',
					'line-width': 2
					}
				},
				{
					'id': 'gl-draw-polygon-stroke-active',
					'type': 'line',
					'filter': ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
					'layout': {
					'line-cap': 'round',
					'line-join': 'round'
					},
					'paint': {
					'line-color': '#00ab5f',
					'line-dasharray': [0.2, 2],
					'line-width': 2
					}
				},
				{
					'id': 'gl-draw-line-inactive',
					'type': 'line',
					'filter': ['all',
					['==', 'active', 'false'],
					['==', '$type', 'LineString'],
					['!=', 'mode', 'static']
					],
					'layout': {
					'line-cap': 'round',
					'line-join': 'round'
					},
					'paint': {
					'line-color': '#3bb2d0',
					'line-width': 2
					}
				},
				{
					'id': 'gl-draw-line-active',
					'type': 'line',
					'filter': ['all',
					['==', '$type', 'LineString'],
					['==', 'active', 'true']
					],
					'layout': {
					'line-cap': 'round',
					'line-join': 'round'
					},
					'paint': {
					'line-color': '#00ab5f',
					'line-dasharray': [0.2, 2],
					'line-width': 2
					}
				},
				{
					'id': 'gl-draw-polygon-and-line-vertex-stroke-inactive',
					'type': 'circle',
					'filter': ['all',
					['==', 'meta', 'vertex'],
					['==', '$type', 'Point'],
					['!=', 'mode', 'static']
					],
					'paint': {
					'circle-radius': 5,
					'circle-color': '#fff'
					}
				},
				{
					'id': 'gl-draw-polygon-and-line-vertex-inactive',
					'type': 'circle',
					'filter': ['all',
					['==', 'meta', 'vertex'],
					['==', '$type', 'Point'],
					['!=', 'mode', 'static']
					],
					'paint': {
					'circle-radius': 3,
					'circle-color': '#00ab5f'
					}
				},
				{
					'id': 'gl-draw-point-point-stroke-inactive',
					'type': 'circle',
					'filter': ['all',
					['==', 'active', 'false'],
					['==', '$type', 'Point'],
					['==', 'meta', 'feature'],
					['!=', 'mode', 'static']
					],
					'paint': {
					'circle-radius': 5,
					'circle-opacity': 1,
					'circle-color': '#fff'
					}
				},
				{
					'id': 'gl-draw-point-inactive',
					'type': 'circle',
					'filter': ['all',
					['==', 'active', 'false'],
					['==', '$type', 'Point'],
					['==', 'meta', 'feature'],
					['!=', 'mode', 'static']
					],
					'paint': {
					'circle-radius': 3,
					'circle-color': '#3bb2d0'
					}
				},
				{
					'id': 'gl-draw-point-stroke-active',
					'type': 'circle',
					'filter': ['all',
					['==', '$type', 'Point'],
					['==', 'active', 'true'],
					['!=', 'meta', 'midpoint']
					],
					'paint': {
					'circle-radius': 7,
					'circle-color': '#fff'
					}
				},
				{
					'id': 'gl-draw-point-active',
					'type': 'circle',
					'filter': ['all',
					['==', '$type', 'Point'],
					['!=', 'meta', 'midpoint'],
					['==', 'active', 'true']],
					'paint': {
					'circle-radius': 5,
					'circle-color': '#00ab5f'
					}
				},
				{
					'id': 'gl-draw-polygon-fill-static',
					'type': 'fill',
					'filter': ['all', ['==', 'mode', 'static'], ['==', '$type', 'Polygon']],
					'paint': {
					'fill-color': '#404040',
					'fill-outline-color': '#404040',
					'fill-opacity': 0.1
					}
				},
				{
					'id': 'gl-draw-polygon-stroke-static',
					'type': 'line',
					'filter': ['all', ['==', 'mode', 'static'], ['==', '$type', 'Polygon']],
					'layout': {
					'line-cap': 'round',
					'line-join': 'round'
					},
					'paint': {
					'line-color': '#404040',
					'line-width': 2
					}
				},
				{
					'id': 'gl-draw-line-static',
					'type': 'line',
					'filter': ['all', ['==', 'mode', 'static'], ['==', '$type', 'LineString']],
					'layout': {
					'line-cap': 'round',
					'line-join': 'round'
					},
					'paint': {
					'line-color': '#404040',
					'line-width': 2
					}
				},
				{
					'id': 'gl-draw-point-static',
					'type': 'circle',
					'filter': ['all', ['==', 'mode', 'static'], ['==', '$type', 'Point']],
					'paint': {
					'circle-radius': 5,
					'circle-color': '#404040'
					}
				}
			],
			userProperties: true,
			// Config snapping featuresss
			snap: true,
			snapOptions: {
				snapPx: 15, // defaults to 15
				snapToMidPoints: true, // defaults to false
			},
			guides: false,
		});
		map.addControl(drawControl);
		map.on('draw.create', updateArea);
		map.on('draw.delete', updateArea);
		map.on('draw.update', updateArea);
		map.on('draw.selectionchange', selectionChange);
		var dynamicLayers = [];
		var handleMapLoad = function handleMapLoad() {
			console.log("mapload");
			for(let [key, val] of Object.entries(map.getStyle().sources)) {
				console.log(key);
				console.log(val.type);
				if (val.type === "geojson") {
					console.log(cloudLayers[key]);
					console.log(val.data);
					console.log(liveLayers[key]);
					if (cloudLayers[key]) {
						updateSource(key);
					}
					else if (typeof val.data === "object" && !liveLayers[key]) {
						if (key != "empty")
							updateLayer(key);
					}
					else if (typeof val.data === "object" && liveLayers[key] && window.platform == "Windows1") {
						if (key != "empty")
							cacheSnapshot(key);
					}
				}
			}
			if (pendingSources == 0)
						sourcesComplete();
			layers = {};
			links = {};
			map.getStyle().layers.map(function (layer) {
				addLayer(layer);
				if (layer.id.startsWith("__"))
					return;
				if (layer.metadata && layer.metadata["geosync:live"]) {
					dynamicLayers.push(layer);
				}
				else if (config.role > 2  && (layer.type == "fill" || layer.type == "line" || layer.type == "symbol" || layer.type == "circle")) {
					if (cloudLayers[layer.source])
						links[layer.id] = "https://geosync.blob.core.windows.net/" + session.account + "/sources/" + layer.source.split("_")[0] + ".geojson";
					else if (!liveLayers[layer.source])
						links[layer.id] = "https://geosync.blob.core.windows.net/" + session.account + "/layers/" + layer.source + ".geojson";
				}
				layers[layer.id] = true;
				if (layer.layout && layer.layout.visibility == "none")
					layers[layer.id] = false;
			})

			map.on('styledata', function() {
				layers = {};
				map.getStyle().layers.map(function (layer) {
					if (layer.id.startsWith("__"))
						return;
					layers[layer.id] = true;
					if (layer.layout && layer.layout.visibility == "none")
						layers[layer.id] = false;
				})
			});
			map.addSource('geolocation', {
			"type": "geojson",
				"data": {
				"type": "FeatureCollection",
				"features": []
				}
			});
			map.addSource('navigation', {
			"type": "geojson",
				"data": {
				"type": "FeatureCollection",
				"features": []
				}
			});
			map.addSource('origin', {
			"type": "geojson",
				"data": {
				"type": "FeatureCollection",
				"features": []
				}
			});
			map.addSource('offset', {
			"type": "geojson",
				"data": {
				"type": "FeatureCollection",
				"features": []
				}
			});

			map.addLayer({
				"id": "__navigation",
				"source": "navigation",
				"type": "line",
				'layout': {
					'line-join': 'round',
					'line-cap': 'round'
				},
				"paint": {
					'line-color': '#007cbf',
					'line-width': 6
				}
			});

			map.addLayer({
				"id": "__offset",
				"source": "offset",
				"type": "line",
				'layout': {
					'line-join': 'round',
					'line-cap': 'round'
				},
				"paint": {
					'line-color': '#ff0000',
					'line-width': 1
				}
			});

			map.addLayer({
				"id": "__origin",
				"source": "origin",
				"type": "circle",
				"paint": {
					"circle-radius": 4,
					"circle-color": "#ff0000"
				}
			});

			var framesPerSecond = 15;
			var initialOpacity = 1
			var opacity = initialOpacity;
			var initialRadius = 6;
			var radius = initialRadius;
			var maxRadius = 18;
			map.addLayer({
				"id": "__geolocation",
				"source": "geolocation",
				"type": "circle",
				"paint": {
					"circle-radius": initialRadius,
					"circle-radius-transition": {duration: 0},
					"circle-opacity-transition": {duration: 0},
					"circle-color": "#007cbf"
				}
			});

			map.addLayer({
				"id": "__geolocation1",
				"source": "geolocation",
				"type": "circle",
				"paint": {
					"circle-radius": initialRadius,
					"circle-color": "#007cbf",
					"circle-stroke-width": 2,
					"circle-stroke-color": "#ffffff"
				}
			});

			function animateMarker(timestamp) {
				setTimeout(function(){
					requestAnimationFrame(animateMarker);

					radius += (maxRadius - radius) / framesPerSecond;
					opacity -= ( .9 / framesPerSecond );

					if (opacity <= 0) {
						radius = initialRadius;
						opacity = initialOpacity;
					}

					map.setPaintProperty('__geolocation', 'circle-radius', radius);
					map.setPaintProperty('__geolocation', 'circle-opacity', opacity);

				}, 1000 / framesPerSecond);

			}

			animateMarker(0);
			mapLoaded = true;
			
			keys.layers = generateID();
		}
		map.once("styledata", handleMapLoad);
		map.loadImage(getServer() + "/assets/marker.png", addClickEvents);
		if (window.platform == "iOS") {
			let [res, err]  = await fetchData("/icons/icons.json");
			if (!err) {
				res.map(function (icon) {
					map.loadImage(getServer() + "/icons/" + icon + ".png", (error, image) => {
						if (!error)
							map.addImage(icon, image, {
								pixelRatio: 2
							});
					});
				})
			}
		}
		map.on("rotate", function () {
			bearing = map.getBearing();
			keys.rotate = generateID();
		})
		map.on("moveend", function () {
			var mapBounds = map.getBounds();
			const north = mapBounds.getNorth();
			const east = mapBounds.getEast();
			const south = mapBounds.getSouth();
			const west = mapBounds.getWest();
			const center = mapBounds.getCenter();
			
			const nw = new mapboxgl.LngLat(west, north);
			const ne = new mapboxgl.LngLat(east, north);
			const se = new mapboxgl.LngLat(east, south);
			const sw = new mapboxgl.LngLat(west, south);
			if (lastDraw) {
					if (lastDraw.bounds.contains(nw) && lastDraw.bounds.contains(ne) && lastDraw.bounds.contains(se) && lastDraw.bounds.contains(sw) && lastDraw.zoom == map.getZoom())
							return;
			}

			const width = nw.distanceTo(ne) * 1.5;
			const height = nw.distanceTo(sw) * 1.5;
			
			if (width > height) {
				mapBounds = center.toBounds(width);
			} else {
				mapBounds = center.toBounds(height);
			}
			lastDraw = { bounds: mapBounds, zoom: map.getZoom() };
			console.log(lastDraw);
			var bounds = mapBounds.getWest() + "," + mapBounds.getSouth() + "," + mapBounds.getEast() + "," + mapBounds.getNorth();

			var sourceList = {};
			dynamicLayers.map(function (layer){
				sourceList[layer.source] = true;
				if ((layer.minzoom && layer.minzoom > map.getZoom()) || map.getLayoutProperty(layer.id, "visibility") == "none")
					sourceList[layer.source] = false;
				Object.keys(sourceList).map(async function (key) {
					if (sourceList[key]) {
						if (window.platform == "Windows1" && snapshotList[key]) {
							let [res, err]  = await fetchData(getLocalServer() + "/layers/" + key + "?bbox=" + bounds);
							if (err) {
								console.log(err);
								return;
							}
							map.getSource(key).setData(res);
						}
						else {
							let [res, err]  = await fetchData("/v1/bbox/layers/" + key + "?bbox=" + bounds, {
								headers: {
									'Authorization': 'Bearer ' + session.token
								}
							});
							if (err) {
								console.log(err);
								return;
							}
							//setCache("layer", key, "", res);
							map.getSource(key).setData(res);
						}
					}
				})
			})
		});
		map.on("zoomend", function () {
			zoom = Math.round(map.getZoom());
		});
		map = map;
	}
	function addClickEvents(error, image) {
		if (error)
			return;
		map.addImage("user-marker", image);
		userLayer = {
			id: "__user",
			type: "symbol",
			source: {
				type: "geojson",
				data: {
					type: "FeatureCollection",
					features: []
				}
			},
			layout: {
				"icon-image": "user-marker",
				"icon-offset": [0, -20]
			},
			paint: {
				"icon-opacity": 0.5
			}
		};
		map.addLayer(userLayer);
		interactiveLayers.push(userLayer.id)

		map.on("mouseenter", userLayer.id, function () {
			if (drawTools || isDrawing || isMeasuring)
				return;

			map.getCanvas().style.cursor = "pointer";
		});

		map.on("mouseleave", userLayer.id, function () {
			if (drawTools || isDrawing || isMeasuring)
				return;

			map.getCanvas().style.cursor = "";
		});

		if (config.role >= 2) {
			var xy1 = {};
			var t2 = 0;
			map.on("touchstart", function (e) {
				xy1 = e.point;
				t2 = performance.now();
			});

			map.on("touchend", function (e) {
				var xy2 = e.point;
				var distance = Math.sqrt(Math.pow((xy2.x - xy1.x), 2) + Math.pow((xy2.y - xy1.y), 2));
				var t1 = performance.now();
				if (t1 - t2 < 1000 || distance > 10 || drawTools)
					return;
				contextHandler(e.lngLat.lat, e.lngLat.lng, userLayer);
			}.bind(this));

			map.on("contextmenu", function (e) {
				e.originalEvent.preventDefault();
				contextHandler(e.lngLat.lat, e.lngLat.lng, userLayer);
			});

			map.addSource("history", {
				type: "geojson",
				data: {
					type: "FeatureCollection",
					features: history
				}
			});

			var historyLayer = {
				id: "reportlog",
				type: "symbol",
				source: "history",
				layout: {
					"text-field": "\uf1ce",
					"text-size": 30,
					"text-font": [
					"Font Awesome Solid"
					],
					"text-offset": [0, 0.2],
					"text-ignore-placement": true,
					"text-allow-overlap": true,
					"visibility": "none"
				},
				paint: {
					"text-color": "#15f4ee"
				},
				"filter": ["==", "$type", "Point"],
			};
        	addHistoryLayer(historyLayer);

			historyLayer = {
				id: "reportlog_line",
				type: "line",
				source: "history",
				layout: {
					"visibility": "none"
				},
				paint: {
					"line-color": "#15f4ee"
				},
				filter: ["==", "$type", "LineString"],
			};
        	addHistoryLayer(historyLayer);

			historyLayer = {
				id: "reportlog_fill",
				type: "fill",
				source: "history",
				paint: {
					"fill-opacity": 0.5,
					"fill-color": "#15f4ee"
				},
				layout: {
					"visibility": "none"
				},
				filter: ["==", "$type", "Polygon"],
			};
        	addHistoryLayer(historyLayer);
		}
		map.on("click", function (e) {
			queryFeature(e.point);
		});
		var xy1 = {};
		var t0 = 0;
		map.on("touchstart", function (e) {
			t0 = performance.now();
			xy1 = e.point;
		});
		map.on("touchend", function (e) {
			var xy2 = e.point;
			var distance = Math.sqrt(Math.pow((xy2.x - xy1.x), 2) + Math.pow((xy2.y - xy1.y), 2));
			var t1 = performance.now();
			if (t1 - t0 > 1000 || distance > 10)
				return;

			queryFeature(e.point);
		});

		map.addSource("measurements", {
			"type": "geojson",
			"data": {
				"type": "FeatureCollection",
				"features": []
			}
		});

		map.addLayer({
			id: '__measure-points',
			type: 'circle',
			source: 'measurements',
			paint: {
				'circle-radius': 5,
				'circle-color': '#000'
			},
			filter: ['in', '$type', 'Point']
		});
		map.addLayer({
			id: '__measure-lines',
			type: 'line',
			source: 'measurements',
			layout: {
				'line-cap': 'round',
				'line-join': 'round'
			},
			paint: {
				'line-color': '#000',
				'line-width': 2.5
			},
			filter: ['in', '$type', 'LineString']
		});
	}
	function addHistoryLayer(historyLayer) {
		map.addLayer(historyLayer);
		groups["__default__"].push(historyLayer);
		var highlight = Object.assign({}, historyLayer);
		highlight.id = "__active__" + highlight.id;
		highlight.layout.visibility = "visible";
		if (!highlight.paint)
			highlight.paint = {};
		if (historyLayer.type == "symbol")
			highlight.paint["text-color"] = "#00ab5f";
		else if (historyLayer.type == "fill")
			highlight.paint["fill-color"] = "#00ab5f";
		else if (historyLayer.type == "line")
			highlight.paint["line-color"] = "#00ab5f";

		highlight.filter = ["==", "history_id", ""];
		interactiveLayers.push(historyLayer.id);

		map.addLayer(highlight);
		map.on("mouseenter", historyLayer.id, function () {
			if (drawTools || isDrawing || isMeasuring)
				return;

			map.getCanvas().style.cursor = "pointer";
		});

		map.on("mouseleave", historyLayer.id, function () {
			if (drawTools || isDrawing || isMeasuring)
				return;

			map.getCanvas().style.cursor = "";
		});
	}
	function updateArea(e) {
		var data = {
			type: "FeatureCollection",
			features: e.features
		};
		if (data.features.length > 0) {
			var feature = data.features[0];
			var label = "User Feature";
			var idField = "_id";
			var selectedLayer = { id: "__draw", metadata: {} };
			
			if (feature.id && feature.id.includes("|")) {
				var ids = feature.id.split("|");
				var layer = map.getLayer(ids[0]);
				if (layer.id == "reportlog" || layer.id == "reportlog_line" || layer.id == "reportlog_fill") {
					label = "Log Feature";
					idField = "history_id";
				}
				else if (layer.metadata && layer.metadata["geosync:label"] && layer.metadata["geosync:id"]) {
					selectedLayer.metadata = layer.metadata; 
					label = feature.properties[layer.metadata["geosync:label"]];
					idField = layer.metadata["geosync:id"];
				}
			}
			else if (feature.geometry.type == "Polygon") {
				var area = turf.default.area(data);
				if (settings.units != "meters")
					area = turf.default.convertArea(area, "meters", settings.units);
				// restrict to area to 2 decimal points
				var rounded_area = Math.round(area*100)/100;

				feature.properties = {};
				feature.properties._id = "user";
				label = rounded_area.toLocaleString() + " " + units[settings.units] + "²";
				feature.properties.area = label;
			}
			else if (feature.geometry.type == "LineString") {
				var length = turf.default.length(data, {units: settings.units});
				// restrict to area to 2 decimal points
				var rounded_length = Math.round(length*100)/100;

				feature.properties = {};
				feature.properties._id = "user";
				label = rounded_length.toLocaleString() + " " + units[settings.units];
				feature.properties.distance = label;
			}
			else if (feature.geometry.type == "Point") {
				feature.properties = {};
				feature.properties._id = "user";
				label = JSON.stringify(feature.geometry.coordinates);
			}
			setTimeout(function () {
				isDrawing = false
			}, 500);
			if (e.type != "draw.delete")
				selectFeature(feature, selectedLayer, label, idField);
			map.getCanvas().style.cursor = "";
		} else {
			deselectFeature();
		}
	}
	function selectionChange (e) {
		var data = {
			type: "FeatureCollection",
			features: e.features
		};
		if (data.features.length > 0) {
			var feature = data.features[0];
			var label = "User Feature";
			var idField = "_id";
			var selectedLayer = { id: "__draw", metadata: {}};

			if (feature.id && feature.id.includes("|")) {
				var ids = feature.id.split("|");
				var layer = map.getLayer(ids[0]);
				if (layer.id == "reportlog" || layer.id == "reportlog_line" || layer.id == "reportlog_fill") {
					label = "Log Feature";
					idField = "history_id";
				}
				else if (layer.metadata && layer.metadata["geosync:label"] && layer.metadata["geosync:id"]) {
					selectedLayer.metadata = layer.metadata; 
					label = feature.properties[layer.metadata["geosync:label"]];
					idField = layer.metadata["geosync:id"];
				}
			} else if (feature.geometry.type == "Polygon") {
				var area = turf.default.area(data);
				if (settings.units != "meters")
					area = turf.default.convertArea(area, "meters", settings.units);
				// restrict to area to 2 decimal points
				var rounded_area = Math.round(area*100)/100;

				feature.properties = {};
				feature.properties._id = "user";
				label = rounded_area.toLocaleString() + " " + units[settings.units] + "²";
				feature.properties.area = label;
			}
			else if (feature.geometry.type == "LineString") {
				var length = turf.default.length(data, {units: settings.units});
				// restrict to area to 2 decimal points
				var rounded_length = Math.round(length*100)/100;

				feature.properties = {};
				feature.properties._id = "user";
				label = rounded_length.toLocaleString() + " " + units[settings.units];
				feature.properties.distance = label;
			}
			else if (feature.geometry.type == "Point") {
				feature.properties = {};
				feature.properties._id = "user";
				label = JSON.stringify(feature.geometry.coordinates);
			}
			setTimeout(function () {
				isDrawing = false
			}, 500);
			selectFeature(feature, selectedLayer, label, idField);
			map.getCanvas().style.cursor = "";
		}
	}
	function queryFeature(point) {
		var width = 10;
		var height = 10;
		var features = map.queryRenderedFeatures([
			[point.x - width / 2, point.y - height / 2],
      [point.x + width / 2, point.y + height / 2]
		], { layers: interactiveLayers });

		if (!features.length) {
			return;
		}

		selected.features = features;
		var feature = features[0];
		var layer = feature.layer;

		if (layer.id == "__user")
			userClickHandler(feature, layer, "User Feature", "_id")
		else if (layer.id == "reportlog" || layer.id == "reportlog_line" || layer.id == "reportlog_fill")
			selectFeature(feature, layer, "Log Feature", "history_id")
		else if (layer.metadata && layer.metadata["geosync:live"]) {
			selectFeature(feature, layer, feature.properties[feature.layer.metadata["geosync:label"]], layer.metadata["geosync:id"])
		}
		else {
			var source = map.getSource(layer.source);
			console.log(source);
			if (source.type == "vector") {
				selectFeature(feature, layer, feature.properties[feature.layer.metadata["geosync:label"]], layer.metadata["geosync:id"])
			} else {
				featureLookup(feature, layer, feature.properties[feature.layer.metadata["geosync:label"]], layer.metadata["geosync:id"])
			}
		}
	}
	function addLayer(layer) {
		if (layer.id.startsWith("__clusters__")) {
			map.on('click', layer.id, function (e) {
				map.flyTo({center: e.lngLat, zoom: map.getZoom() + 1});
			});

			map.on("mouseenter", layer.id, function () {
				if (drawTools || isDrawing || isMeasuring)
					return;

				map.getCanvas().style.cursor = "pointer";
			});

			map.on("mouseleave", layer.id, function () {
				if (drawTools || isDrawing || isMeasuring)
					return;

				map.getCanvas().style.cursor = "";
			});
		}

		if (!layer.metadata || !layer.metadata["geosync:interactive"] || layer.id.startsWith("__"))
			return;

		interactiveLayers.push(layer.id);
		interactiveLayers = interactiveLayers;
		addHighlightLayer(layer);

		map.on("mouseenter", layer.id, function () {
			if (drawTools || isDrawing || isMeasuring)
				return;

			map.getCanvas().style.cursor = "pointer";
		});

		// Change it back to a pointer when it leaves.
		map.on("mouseleave", layer.id, function () {
			if (drawTools || isDrawing || isMeasuring)
				return;

			map.getCanvas().style.cursor = "";
		});
	}
	function addHighlightLayer(layer) {
		var highlight = Object.assign({}, layer);
		highlight.id = "__active__" + highlight.id;
		if (!highlight.layout)
			highlight.layout = {};
		highlight.layout.visibility = "visible";
		if (!highlight.paint)
			highlight.paint = {}
		if (highlight.type === "circle") {
			highlight.paint["circle-color"] = "#00ab5f"
		}
		else if (highlight.type === "symbol") {
			if (highlight.layout["icon-image"]) {
				highlight.layout = {};
				highlight.type = "circle";
				highlight.paint = {};
				highlight.paint["circle-radius"] = 20;
				highlight.paint["circle-opacity"] = 0;
				highlight.paint["circle-stroke-opacity"] = 1;
				highlight.paint["circle-stroke-width"] = 5;
				highlight.paint["circle-stroke-color"] = "#00ab5f";
			} else {
				highlight.paint["text-color"] = "#00ab5f"
			}
		}
		else if (highlight.type === "line") {
			highlight.paint["line-color"] = "#00ab5f"
		}
		else if (highlight.type === "fill") {
			highlight.paint["fill-color"] = "#00ab5f";
			highlight.paint["fill-opacity"] = 0.5;
		}
		else if (highlight.type === "fill-extrusion") {
			highlight.paint["fill-extrusion-color"] = "#00ab5f";
			highlight.paint["fill-opacity"] = 0.5;
		}
		highlight.filter = ["==", layer.metadata["geosync:id"], ""]
		map.addLayer(highlight);
	}
	function contextHandler(lat, lng, userLayer) {
		var feature = {
			type: "Feature",
			properties: {
				_id: "user",
				_latitude: lat,
				_longitude: lng
			},
			geometry: {
				type: "Point",
				"coordinates": [
					lng,
					lat
				]
			}
		};
		updateUserLayer(feature);
		userClickHandler(feature, userLayer, "User Feature", "_id");
		var kyurl = new URL("https://kyraster.ky.gov/arcgis/rest/services/ElevationServices/Ky_DEM_KYAPED_5FT/ImageServer/identify");
		var kyparams = {
          returnGeometry: false,
          returnCatalogItems: false,
          geometry: JSON.stringify({
							x: lng,
							y: lat,
							spatialReference: {
									wkid: 4326
							}
					}),
          geometryType: "esriGeometryPoint",
          f: "json"
				}
		kyurl.search = new URLSearchParams(kyparams).toString();
		fetch(kyurl)
			.then(
				function(response) {
					if (response.status !== 200) {
						console.log('Looks like there was a problem. Status Code: ' +
							response.status);
						return;
					}

					// Examine the text in the response
					response.json().then(function(data) {
						feature.properties._kyaped_dem = data.value;
						feature.properties._ky_oblique = "https://explore.kyfromabove.ky.gov/?ll=" + lat + "," + lng;
						updateUserLayer(feature);
						userClickHandler(feature, userLayer, "User Feature", "_id");
					});
				}
			)
			.catch(function(err) {
				console.log('Fetch Error :-S', err);
			});
		var usgsurl = new URL("https://nationalmap.gov/epqs/pqs.php");
		var usgsparams = {
			x: lng,
			y: lat,
			units: "feet",
			output: "json"
		}
		usgsurl.search = new URLSearchParams(usgsparams).toString();
		fetch(usgsurl)
			.then(
				function(response) {
					if (response.status !== 200) {
						console.log('Looks like there was a problem. Status Code: ' +
							response.status);
						return;
					}

					// Examine the text in the response
					response.json().then(function(data) {
						feature.properties._usgs_dem = data.USGS_Elevation_Point_Query_Service.Elevation_Query.Elevation;
						updateUserLayer(feature);
						userClickHandler(feature, userLayer, "User Feature", "_id");
					});
				}
			)
			.catch(function(err) {
				console.log('Fetch Error :-S', err);
			});
	}
	function updateUserLayer(feature) {
		var source = map.getSource("__user");
		if (source) {
			source.setData({
				type: "FeatureCollection",
				features: [feature]
			});
		}
	}
	function clearUserLayer(feature) {
		var source = map.getSource("__user");
		if (source) {
			source.setData({
				type: "FeatureCollection",
				features: []
			});
		}
	}
	function userClickHandler(feature, layer, label, idField) {
		if (drawTools || isDrawing || isMeasuring)
			return;

		selectFeature(feature, layer, label, idField);
		map.setPaintProperty("__user", "icon-opacity", 1);
	}
	function reselectFeature() {
		deselectFeature(true);
		map.setFilter("__active__" + selected.layer.id, ["==", selected.idField, selected.feature.properties[selected.idField]]);
	}
	async function featureLookup(feature, layer, label, idField) {
		var found = false;
		if (layer.source) {
			var source = map.getSource(layer.source);
			if (source) {
				var source_id = "layer_" + source.id;
				openIfExists(source_id, async function (exists) {
					console.log(exists);
					if (exists) {
						var features = match(feature.properties[idField], idField);
						console.log(features);
						if (features.length == 0)
							return;
						feature = features[0];
						selectFeature(feature, layer, label, idField);
					}
				})
			}
		}
	}
	async function selectFeature(feature, layer, label, idField, bypass) {
		if ((drawTools && layer.id != "__draw") || (isDrawing && layer.id != "__draw") || (isMeasuring && !bypass)) {
			return;
		}
		console.log(feature);
		deselectFeature(true);

		selected.feature = feature;

		selected.layer = layer;
		selected.label = label;
		selected.idField = idField;

		if (feature.geometry && feature.geometry.type == "Point") {
			feature.properties._longitude = feature.geometry.coordinates[0]
			feature.properties._latitude = feature.geometry.coordinates[1]
		}

		if (layer.id == "__draw" || layer.id == "__user")
			return;
		
		map.setFilter("__active__" + layer.id, ["==", idField, feature.properties[idField]]);

		map.getSource("navigation").setData({
				type: "FeatureCollection",
				features: []
			});
		if (navControl)
			map.removeControl(navControl);
		if (location.enabled) {
			var center = getCenter(selected.feature)
			var midpoint = [(location.longitude + center[0]) / 2, (location.latitude + center[1]) / 2];
			var lineString = {
				type: "Feature",
				properties: location,
				geometry: {
					type: "LineString",
					coordinates: [[location.longitude, location.latitude], center]
				}
			}
			const distance = turf.default.length(lineString, {units: settings.units});
			var rounded_length = Math.round(distance*100)/100;

			map.getSource("navigation").setData({
				type: "FeatureCollection",
				features: [lineString]
			});
			navControl = new LabelButton('<i class="icon icon-resize-horiz"></i> ' + rounded_length.toLocaleString() + " " + units[settings.units])
			map.addControl(navControl, "top-left");
		}
		/*if (layer.id !== "__user" && layer.id !== "reportlog" && layer.id !== "reportlog_line" && layer.id !== "reportlog_fill") {
			agent.Map.media().then(function (result) {
				if (result.status === "success")
				commonStore.setMedia(result.data.media);
			}.bind(this))
			var ids = layer.source.split("_");
			if (ids.length > 1) {
				agent.Map.history(ids[1], feature.properties[idField]).then(function (result) {
				if (result.status === "success")
					commonStore.setHistory(result.data.history);
				}.bind(this))
			}
		}*/
	}
	function deselectFeature(selection) {
		if (selected.layer) {
			if (selected.layer.id == "__user") {
				map.setPaintProperty("__user", "icon-opacity", 0.5);
			}
			else if (selected.layer.id == "reportlog" || selected.layer.id == "reportlog_fill" || selected.layer.id == "reportlog_line") {
				map.setFilter("__active__" + selected.layer.id, ["==", "history_id", ""]);
			}
			else if (selected.layer.metadata) {
				var idField = selected.layer.metadata["geosync:id"];
				if (map.getLayer("__active__" + selected.layer.id))
					map.setFilter("__active__" + selected.layer.id, ["==", idField, ""]);
			}
		}
		if (!selection) {
			selected = {
				index: 0,
				layer: null,
				feature: null,
				label: "",
				idField: "",
				features: []
			}
			map.getSource("navigation").setData({
				type: "FeatureCollection",
				features: []
			});
			if (navControl)
				navControl.remove()
			isOpen.callout = false;
		}
		keys.feature = generateID();
	}
	function updateSource(id) {
		console.log("updateSource");
		pendingSources++;
		var key = id.split("_")[0];
		var decremented = false;
		if (hasCache("source", key)) {
			getCache("source", key, function (sourceData) {
				var source = map.getSource(id)
				map.getSource(id).setData(sourceData);
				console.log("dec");
				console.log(key);
				if (!decremented) {
					pendingSources--;
					if (pendingSources == 0)
						sourcesComplete();
				}
			})
		}
		cachedFetch("/v1/sources/" + key, "source", session.id, key, {
            headers: {
                'Authorization': 'Bearer ' + session.token
            }
        }, function ([res, err]) {
			if (err) {
				console.log(err);
			}
			else if (res) {
				map.getSource(id).setData(res);
			}
			console.log("dec");
			console.log(key);
			if (!decremented) {
				pendingSources--;
				if (pendingSources == 0)
					sourcesComplete();
			}
		});
	}
	function updateLayer(id) {
		console.log("updateLayer");
		pendingSources++;
		var decremented = false;
		if (hasCache("layer", id)) {
			getCache("layer", id, function (layerData) {
				var source = map.getSource(id)
				map.getSource(id).setData(layerData);
				console.log("dec");
				console.log(id);
				if (!decremented)
					pendingSources--;
					if (pendingSources == 0)
						sourcesComplete();
			})
		}
		cachedFetch("/v1/layers/" + id, "layer", session.id, id, {
            headers: {
                'Authorization': 'Bearer ' + session.token
            }
        }, function ([res, err]) {
			if (err) {
				console.log(err);
			}
			else if (res) {
				map.getSource(id).setData(res);
			}
				console.log("dec");
				console.log(id);
			if (!decremented) {
				pendingSources--;
				if (pendingSources == 0)
					sourcesComplete();
			}
		});
	}
	function cacheSnapshot(id) {
		pendingSources++;
		fetchSnapshot("/snapshots/" + id, "snapshot", session.token, id, {
						method: "POST",
						headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
						body: JSON.stringify({
							id: id,
							container: session.container,
							key: session.query
						})
        }, function ([res, err]) {

			if (!err) {
				snapshotList[id] = true;
			}
			pendingSources--;
			if (pendingSources == 0)
				sourcesComplete();
		});
	}
	function zoomTo(layer, feature) {
		var label = "User Feature";
		var idField = "_id";
		var labelField = "";
		if (layer.id == "reportlog" || layer.id == "reportlog_line" || layer.id == "reportlog_fill") {
			label = "Log Feature";
			idField = "history_id";
		}
		else {
			if (layer.metadata && layer.metadata["geosync:id"])
				idField = layer.metadata["geosync:id"];
			if (layer.metadata && layer.metadata["geosync:label"]) {
				labelField = layer.metadata["geosync:label"];
				if (feature && feature.properties && feature.properties[labelField])
					label = feature.properties[labelField];
			}
		}
		map.setFilter("__active__" + layer.id, ["==", idField, feature.properties[idField]]);
		selectFeature(feature, layer, label, feature.properties[idField]);

		if (feature.geometry.type === "Point") {
			var zoom = 18;
			map.flyTo({center: feature.geometry.coordinates, zoom: zoom});
		}
		else if (feature.geometry.type === "LineString") {
			var geometry = helpers.lineString(feature.geometry.coordinates);
			var bounds = bbox(geometry);
			map.fitBounds(bounds, {
					padding: 100
			});
		}
		else if (feature.geometry.type === "Polygon") {
			var geometry = helpers.polygon(feature.geometry.coordinates);
			var bounds = bbox(geometry);
			map.fitBounds(bounds, {
					padding: 100
			});
		}
		else if (feature.geometry.type === "MultiLineString") {
			var geometry = helpers.multiLineString(feature.geometry.coordinates);
			var bounds = bbox(geometry);
			map.fitBounds(bounds, {
					padding: 100
			});
		}
		else if (feature.geometry.type === "MultiPolygon") {
			var geometry = helpers.multiPolygon(feature.geometry.coordinates);
			var bounds = bbox(geometry);
			map.fitBounds(bounds, {
					padding: 100
			});
		}
		else if (feature.geometry.type === "MultiPoint") {
			var geometry = helpers.multiPoint(feature.geometry.coordinates);
			var bounds = bbox(geometry);
			map.fitBounds(bounds, {
					padding: 100
			});
		}
	}
	function sourcesComplete() {
		console.log("sourcesLoaded");
		if (sourcesLoaded)
			return;
		sourcesLoaded = true;
		map.addControl(new mapboxgl.AttributionControl(), "bottom-right");
		var inner = document.querySelector(".maplibregl-ctrl-attrib-inner");
		if (inner)
			inner.textContent = inner.textContent.replaceAll("source", "").replaceAll("dynamic", "").replaceAll(" | ", "");
		var urlParams = new URLSearchParams(window.location.search);
		var initialLayer = urlParams.get("layer");
		var initialFeature = urlParams.get("feature");
		console.log("sources completed")
		console.log(initialLayer);
		console.log(initialFeature);
		if (initialLayer && initialFeature) {
			var layer = map.getLayer(initialLayer);
			console.log(layer);
       if (!layer)
         return;
			 var source = map.getSource(layer.source);
			 console.log(source);
       if (!source || source.type != "geojson" || !source._data)
				 return;
				 
			 var feature = null;
       var idField = layer.metadata["geosync:id"];
			var id = initialFeature;
			if (!isNaN(id)) {
				id = parseInt(id);
			}
		
		console.log(source.id);
			var source_id = "layer_" + source.id;
			openIfExists(source_id, function () {
				var features = match(id, idField);
				console.log(features);
				if (features.length == 0)
					return;
				feature = features[0];

				console.log(feature);

				map.setFilter("__active__" + layer.id, ["==", idField, id]);
				selectFeature(feature, layer, feature.properties[layer.metadata["geosync:label"]], feature.properties[idField]);

				if (feature.geometry.type === "Point") {
						var zoom = 18;
						map.flyTo({center: feature.geometry.coordinates, zoom: zoom});
					}
					else if (feature.geometry.type === "LineString") {
						var geometry = helpers.lineString(feature.geometry.coordinates);
						var bounds = bbox(geometry);
						map.fitBounds(bounds, {
								padding: 100
						});
					}
					else if (feature.geometry.type === "Polygon") {
						var geometry = helpers.polygon(feature.geometry.coordinates);
						var bounds = bbox(geometry);
						map.fitBounds(bounds, {
								padding: 20
						});
					}
					else if (feature.geometry.type === "MultiLineString") {
						var geometry = helpers.multiLineString(feature.geometry.coordinates);
						var bounds = bbox(geometry);
						map.fitBounds(bounds, {
								padding: 100
						});
					}
					else if (feature.geometry.type === "MultiPolygon") {
						var geometry = helpers.multiPolygon(feature.geometry.coordinates);
						var bounds = bbox(geometry);
						map.fitBounds(bounds, {
								padding: 100
						});
					}
					else if (feature.geometry.type === "MultiPoint") {
						var geometry = helpers.multiPoint(feature.geometry.coordinates);
						var bounds = bbox(geometry);
						map.fitBounds(bounds, {
								padding: 100
						});
					}
				});
			}
	}
	function createToast(data) {
		if (!data.id)
			data.id = generateID();
		toasts.push(data);
		keys.toast = generateID();
		if (!data.sticky) {
			setTimeout(function () {
				var index = toasts.findIndex(obj => obj.id == data.id);
				if (index > -1) {
					toasts.splice(index, 1);
					keys.toast = generateID();
				}
			}, 5000);
		}
	}
	function closeToast(id) {
		var index = toasts.findIndex(obj => obj.id == id);
		if (index > -1) {
			toasts.splice(index, 1);
			keys.toast = generateID();
		}
	}
	const sleep = m => new Promise(r => setTimeout(r, m))
	async function poll() {
				console.log("polling");
		console.log(polling);

		var errorDelay = 30000;
		if (!polling) {
			await sleep(5000);
			await poll();
			return;
		}
		var timeout = 45;  // in seconds
		var optionalSince = "&since_time=" + lastPoll;


		console.log(optionalSince);
		let [res, err] = await fetchData("/v1/events?timeout=" + timeout + "&category=" + session.account + optionalSince, {
			headers: {
				"Authorization": "Bearer " + session.token
			}
		})

		console.log(res);
		console.log(err);

		if (err) {
			await sleep(errorDelay);
			await poll();
			return;
		}

		if (res.error) {
			await sleep(errorDelay);
			await poll();
			return;
		}

		if (res && res.events && res.events.length > 0) {
			var obj = {};
			res.events.map(function (event) {
				if (event.data.old_val && event.data.new_val)
					obj["b_" + event.data.type + "_" + event.data.old_val.id + "_" + event.data.new_val.id] = event;
				else if (event.data.new_val)
					obj["a_" + event.data.type + "_" +event.data.new_val.id] = event;
				else if (event.data.old_val)
					obj["c_" + event.data.type + "_" +event.data.old_val.id] = event;
			})
			
			var hasUpdates = false;
			Object.keys(obj).sort().map(async function (key) {
				var event = obj[key];
				lastPoll = event.timestamp;
				if (event.data.type == "source" || event.data.type == "layer") {
					console.log(event.data.new_val);
					if (!liveLayers[event.data.id + "_" + event.data.layer]) {
						var path = "";
						if (event.data.type == "source") {
							path = event.data.id;
						}
						else {
							path = event.data.id + "_" + event.data.layer;
						}
						layerChanged(event.data.type + "_" + path, event.data.idField, event.data.old_val, event.data.new_val, function(data) {
							setCache(event.data.type, path, "", data);
							var source = path;
							if (event.data.type == "source")
								source = event.data.id + "_" + event.data.layer;
							map.getSource(source).setData(data);
							/*if (event.data.new_val && selected.layer && selected.feature && source == selected.layer.source && selected.feature.properties && selected.idField && selected.feature.properties && selected.feature.properties[selected.idField] == event.data.new_val.properties[selected.idField]) {
								reselectFeature();
							}*/
						});
					}
					else {
						var data = map.getSource(event.data.id + "_" + event.data.layer)._data;
						var old_val = event.data.old_val;
						var new_val = event.data.new_val;
						if (old_val && new_val) {
								var index = data.features.findIndex(x => x.properties[event.data.idField] === old_val.properties[event.data.idField]);
								if (index == -1) {
									data.features.push({
										properties: new_val.properties,
										geometry: new_val.geometry,
										type: "Feature"
									})
								}
								else {
									var properties = {
										...data.features[index].properties,
										...new_val.properties
									}
									data.features[index].properties = properties;
									if (new_val.geometry) {
										data.features[index].geometry = new_val.geometry
									}
								}
								map.getSource(event.data.id + "_" + event.data.layer).setData(data);
						}
						else if (new_val) {
							data.features.push({
								properties: new_val.properties,
								geometry: new_val.geometry,
								type: "Feature"
							})
							map.getSource(event.data.id + "_" + event.data.layer).setData(data);
						}
						else if (old_val) {
							var index = data.features.findIndex(x => x.properties[event.data.idField] === old_val.properties[event.data.idField]);
							if (index != 1) {
								data.features.splice(index, 1);
								map.getSource(event.data.id+ "_" + event.data.layer).setData(data);
							}
						}
					}
					hasUpdates = true;
				}
			})
			if (hasUpdates) {
				keys.layers = generateID();
				createToast({
					message: "Layer data has changed."
				});
				hasUpdates = false;
			}
		}
		await poll();		
	}

	function hasLocation(data) {
		if (data.exists)
			xmlLocation = true;
	}

	function gotLocation(data) {
		updateLocation(data);
	}

	function locationHandler(e) {
		e.currentTarget.blur();
		if (window.platform == "Android") {
			androidAdapter.checkGeolocationPermission();
		}
		if (location.enabled) {
			clearInterval(locationLineInt);
			map.removeControl(geolocateControl);
			map.addControl(geolocateControl, "top-right");
			if (settings.location == "ws") {
				if (window.platform == "iOS") {
					try {
            webkit.messageHandlers.wsHandler.postMessage("geolocation_off");
					} catch(err) {
							console.error(err);
							createToast({
								message: "Geolocation error."
							});
					}
				}
				else {
					socket.close(1000, "User closed");
					socket = null;
				}
			}
			else if (xmlLocation) {
				if (locationTimer)
					window.clearInterval(locationTimer);
			}
			else if (settings.location == "gnss") {
				if (window.platform == "Android") {
					androidAdapter.disconnect();
				}
				else if (window.platform == "iOS") {
					try {
            webkit.messageHandlers.gnssHandler.postMessage("geolocation_off");
					} catch(err) {
							console.error(err);
							createToast({
								message: "Geolocation error."
							});
					}
				}
			}
			else if (settings.location == "eos") {
				if (window.platform == "iOS") {
					try {
            webkit.messageHandlers.eosHandler.postMessage("geolocation_off");
					} catch(err) {
							console.error(err);
							createToast({
								message: "Geolocation error."
							});
					}
				}
			}
			else {
				if (window.platform == "Android") {
					androidAdapter.stopLocation();
				}
				else if (window.platform == "iOS") {
					try {
            webkit.messageHandlers.locationHandler.postMessage("geolocation_off");
					} catch(err) {
							console.error(err);
							createToast({
								message: "Geolocation error."
							});
					}
				}
			}
			location.enabled = false;
			firstLocation = true;
			location.status = "";
			location.label = "off";
			if (accuracyControl)
				map.removeControl(accuracyControl);
			accuracyControl = null;
			if (navControl)
			  map.removeControl(navControl);
			navControl = null;
			map.getSource("geolocation").setData({
				type: "FeatureCollection",
				features: []
			});
			map.getSource("navigation").setData({
				type: "FeatureCollection",
				features: []
			});
			map.getSource("origin").setData({
				type: "FeatureCollection",
				features: []
			});
			map.getSource("offset").setData({
				type: "FeatureCollection",
				features: []
			});
		}
		else {
			location.enabled = true;
			location.status = "away";
			location.label = "stale";
			if (location.latitude && location.longitude) {
				map.getSource("geolocation").setData({
					type: "FeatureCollection",
					features: [{
						type: "Feature",
						properties: location,
						geometry: {
						type: "Point",
						coordinates: [location.longitude, location.latitude]
						}
					}]
				});
				map.getSource("navigation").setData({
					type: "FeatureCollection",
					features: []
				});
				map.getSource("origin").setData({
					type: "FeatureCollection",
					features: []
				});
				map.getSource("offset").setData({
					type: "FeatureCollection",
					features: []
				});
				if (navControl)
					map.removeControl(navControl);
				if (selected.feature) {
					var center = getCenter(selected.feature)
					var midpoint = [(location.longitude + center[0]) / 2, (location.latitude + center[1]) / 2];
					var lineString = {
						type: "Feature",
						properties: location,
						geometry: {
							type: "LineString",
							coordinates: [[location.longitude, location.latitude], center]
						}
					}
					const distance = turf.default.length(lineString, {units: settings.units});
					var rounded_length = Math.round(distance*100)/100;

					map.getSource("navigation").setData({
						type: "FeatureCollection",
						features: [lineString]
					});
					navControl = new LabelButton('<i class="icon icon-resize-horiz"></i> ' + rounded_length.toLocaleString() + " " + units[settings.units])
					map.addControl(navControl, "top-left");
				}
				if (accuracyControl)
					map.removeControl(accuracyControl);
				let icon = '<i class="icon icon-location"></i>';
				if (location.origin) {
					icon = '<i class="icon icon-copy"></i> ';
				}
				accuracyControl = new LabelButton(icon + location.accuracy + " ft")
				map.addControl(accuracyControl, "top-left");
				if (settings.offset) {
					map.getSource("origin").setData({
						type: "FeatureCollection",
						features: [{
							type: "Feature",
							properties: location,
							geometry: {
								type: "Point",
								coordinates: [location.origin.longitude, location.origin.latitude]
							}
						}]
					});
					var lineString = {
						type: "Feature",
						properties: location,
						geometry: {
							type: "LineString",
							coordinates: [[location.longitude, location.latitude], [location.origin.longitude, location.origin.latitude]]
						}
					}
					map.getSource("offset").setData({
						type: "FeatureCollection",
						features: [lineString]
					});
				}
			}
			if (settings.location == "ws") {
				if (window.platform == "iOS") {
					try {
            webkit.messageHandlers.wsHandler.postMessage("geolocation_on");
					} catch(err) {
						console.error(err);
						createToast({
              message: "Geolocation error."
            });
					}
				}
				else {
					socket = new WebSocket("ws://127.0.0.1:9635");

					socket.onmessage = function(event) {
						//console.log(event);
						let location = JSON.parse(event.data);
						delete location.satelliteView;
						updateLocation(location);
					};

					socket.onerror = function(error) {
						createToast({
							message: "Geolocation error."
						});
					};
				}
			}
			else if (xmlLocation) {
				window.chrome.webview.postMessage(JSON.stringify({
						type: "app:location",
				}))
				locationTimer = window.setInterval(function() {
				window.chrome.webview.postMessage(JSON.stringify({
						type: "app:location",
				}))}, 5000);

			}
			else if (settings.location == "gnss") {
				if (window.platform == "Android") {
					var result = androidAdapter.connect(settings.device.match(/.{1,2}/g).join("-"));
					createToast({
						message: result
					});
				}
				else if (window.platform == "iOS") {
					try {
            webkit.messageHandlers.serialHandler.postMessage(settings.serial);
            webkit.messageHandlers.gnssHandler.postMessage("geolocation_on");
					} catch(err) {
						console.error(err);
						createToast({
              message: "Geolocation error."
            });
					}
				}
			}
			else if (settings.location == "eos") {
				if (window.platform == "iOS") {
					try {
            webkit.messageHandlers.eosHandler.postMessage("geolocation_on");
					} catch(err) {
						console.error(err);
						createToast({
              message: "Geolocation error."
            });
					}
				}
			}
			else {
				if (window.platform == "Android") {
					androidAdapter.getLocation();
				}
				else if (window.platform == "iOS") {
					if (window.platform == "iOS") {
						try {
							webkit.messageHandlers.locationHandler.postMessage("geolocation_on");
						} catch(err) {
							console.error(err);
							createToast({
								message: "Geolocation error."
							});
						}
					}
				}
				else {
					geolocateControl.trigger();
				}
			}
		}
	}

	function deselectHandler(e) {
		e.currentTarget.blur();
		if (selected.layer.id == "__user") {
			clearUserLayer();
		}
		deselectFeature();
	}

	function removeHandler(e) {
		e.currentTarget.blur();
		if (selected.layer.id == "__user") {
			clearUserLayer();
		}
		else if (selected.layer.id == "__draw") {
			drawControl.trash();
		}
		deselectFeature();
	}

	function reportHandler(e) {
		e.currentTarget.blur();
		if (editFeature) {
			let feature = drawControl.get(editFeature);
			if (feature) {
				updateArea({
					features: [feature]
				})
			}
		}
		if (forms.length == 1 || (selected.layer.metadata && selected.layer.metadata["geosync:form"])) {
			var form = forms[0];
			if (selected.layer.metadata && selected.layer.metadata["geosync:form"]) {
				form = forms.find(x => x.id == selected.layer.metadata["geosync:form"]);
			}
			if (form) {
				if (form.history && selected.layer && selected.layer.id != "__draw" && selected.layer.id != "__user") {
					modal = {
						view: "option",
						message: "Append a new history record?",
						handler: function responseHandler(data, response) {
								data.view = "form";
								data.history = response;
								modal = data;
						},
						data: {
							form: form,
							mapId: mapId,
							mapName: config.style.name,
							feature: selected.feature,
							layer: selected.layer,
							location: location,
							map: map,
							forms: forms
						}
					}
				}
				else {
					modal = {
						view: "form",
						form: form,
						mapId: mapId,
						mapName: config.style.name,
						feature: selected.feature,
						layer: selected.layer,
						location: location,
						map: map,
						forms: forms
					}
				}
			}
		}
		else {
			modal = {
				view: "forms",
				forms: forms,
				mapId: mapId,
				mapName: config.style.name,
				feature: selected.feature,
        layer: selected.layer,
				location: location,
				map: map
			}
		}
	}

	function geoReportHandler(e) {
		e.currentTarget.blur();
		if (forms.length == 1) {
			modal = {
				view: "form",
				form: forms[0],
				mapId: mapId,
				mapName: config.style.name,
				feature: null,
        layer: null,
				location: location,
				map: map
			}
		}
		else {
			modal = {
				view: "forms",
				forms: forms,
				mapId: mapId,
				mapName: config.style.name,
				feature: null,
        layer: null,
				location: location,
				map: map
			}
		}
	}

	function editHandler(e) {
		e.currentTarget.blur();
		var feature = getHistory(selected.feature.properties.history_id);
		if (!feature)
			return;
		var form = forms.find(x => x.id === feature.metadata.form_id);
		if (form) {
			modal = {
				view: "form",
				form: form,
				mapId: mapId,
				mapName: config.style.name,
				feature: feature,
        layer: selected.layer,
				location: location,
				edit: true,
				map: map
			}
		}
		else
			createToast({
				message: "Form not found"
			});
	}
	function measureHandler() {
		//e.currentTarget.blur();
		if (isMeasuring) {
			var empty = {
				"type": "FeatureCollection",
				"features": []
			}
			measurements = {
				"type": "FeatureCollection",
				"features": []
			};
			linestring = {
				"type": "Feature",
				"properties": {
					"_id": "user"
				},
				"geometry": {
					"type": "LineString",
					"coordinates": []
				}
			};
			map.getSource("measurements").setData(empty);
			map.off("click", measure.click);
			map.off("mousemove", measure.move);
			map.off("touchstart", measure.start);
			map.off("touchend", measure.end);
			map.getCanvas().style.cursor = "";
			deselectFeature();
			isMeasuring = false;
		}
		else {
			deselectFeature();
			measure.click = function (e) {
				var features = map.queryRenderedFeatures(e.point, { layers: ["__measure-points"] });
				if (measurements.features.length > 1) measurements.features.pop();
				if (features.length) {
					var id = features[0].properties.id;
					measurements.features = measurements.features.filter(function(point) {
						return point.properties.id !== id;
					});
				} else {
					var point = {
						"type": "Feature",
						"geometry": {
							"type": "Point",
							"coordinates": [
								e.lngLat.lng,
								e.lngLat.lat
							]
						},
						"properties": {
							"id": String(new Date().getTime())
						}
					};

					measurements.features.push(point);
				}

				console.log(measurements);
				if (measurements.features.length > 1) {
					linestring.geometry.coordinates = measurements.features.map(function(point) {
						return point.geometry.coordinates;
					});

					measurements.features.push(linestring);

					// Populate the distanceContainer with total distance
					var value = turf.default.lineDistance(linestring);
					if (settings.units != "kilometers")
						value = turf.default.convertLength(value, "kilometers", settings.units).toLocaleString() + " " + units[settings.units];
					selectFeature(linestring, { id: "__draw" }, value, "_id", true);
				}

				map.getSource("measurements").setData(measurements);
			}
			measure.move = function(e) {
				var features = map.queryRenderedFeatures(e.point, { layers: ['__measure-points'] });
				map.getCanvas().style.cursor = (features.length) ? 'pointer' : 'crosshair';
			}
			var t0 = 0;
			var xy1 = {};
			measure.start = function (e) {
				xy1 = e.point;
				t0 = performance.now();
			}
			measure.end = function (e) {
				var xy2 = e.point;
				var distance = Math.sqrt(Math.pow((xy2.x - xy1.x), 2) + Math.pow((xy2.y - xy1.y), 2));
				var t1 = performance.now();
				if (t1 - t0 > 1000 || distance > 10)
					return;
				measure.click(e);
			}

			map.on("click", measure.click);
			map.on("mousemove", measure.move);
			map.on("touchstart", measure.start);
			map.on("touchend", measure.end);
			isMeasuring = true;
		}
	}

	function snapHandler(e) {
		e.currentTarget.blur();
		if (selected.feature) {
				drawControl.add(selected.feature);
				if (selected.feature.geometry.type != "Point") {
					snapFeatures.push(selected.feature);
				}
				else {
					snapFeatures.push({
						type: "Feature",
						properties: {},
						geometry: {
							type: "LineString",
							coordinates: [
								selected.feature.geometry.coordinates,
								selected.feature.geometry.coordinates
							]
						}
					});
				}
			}
	}
	
	function drawHandler(mode) {
		//e.currentTarget.blur();
		var from = null;
		if (isDrawing) {
			map.getCanvas().style.cursor = "";
			var features = drawControl.getAll().features;
			if (features && features.length > 0) {
				var featureId = features[features.length - 1].id;
				drawControl.changeMode("simple_select", { featureIds: [featureId] });
			}
			isDrawing = false;
		}
		else {
			if (selected.feature) {
				var ids = [];
				if (selected.feature.geometry.type != "Point") {
					ids = drawControl.add(selected.feature);
				}
				else {
					ids = drawControl.add({
						id: generateID(),
						type: "Feature",
						properties: {},
						geometry: {
							type: "LineString",
							coordinates: [
								selected.feature.geometry.coordinates,
								selected.feature.geometry.coordinates
							]
						}
					})
				}
				snapIds = snapIds.concat(ids);
			}
			editFeature = null
			deselectFeature();
			drawControl.changeMode("simple_select", { featureIds: [] });
			map.getCanvas().style.cursor = "crosshair";
			drawControl.changeMode(mode);
			isDrawing = true;
		}
	}

	function attachHandler(e) {
		e.currentTarget.blur();
		modal = {
			view: "mediaUpload",
			feature: selected.feature,
			layer: selected.layer,
			location: location,
			role: config.role
		}
	}

	function profileHandler(e) {
		e.currentTarget.blur();
		modal = {
			view: "profile",
			feature: selected.feature
		}
	}

	function editGeomLocationHandler() {
		if (!selected.layer)
			return;
		selected.feature.id = selected.layer.id + "|" + generateID();
		selected.feature.geometry.coordinates = [location.longitude, location.latitude];
		var ids = drawControl.add(selected.feature);
		var feature = selected.feature;
		if (selected.feature.geometry.type == "Point") {
			editFeature = ids[0]
			drawControl.changeMode("simple_select", { featureIds: ids });
		}
		
		var label = "User Feature";
		var idField = "_id";
		var labelField = "";

		var layer = selected.layer;
		if (layer.id == "reportlog" || layer.id == "reportlog_line" || layer.id == "reportlog_fill") {
			label = "Log Feature";
			idField = "history_id";
		}
		else {
			if (selected.layer.metadata && selected.layer.metadata["geosync:id"])
				idField = selected.layer.metadata["geosync:id"];
			if (selected.layer.metadata && selected.layer.metadata["geosync:label"]) {
				labelField = selected.layer.metadata["geosync:label"];
				if (selected.feature && selected.feature.properties[labelField])
					label = selected.feature.properties[labelField];
			}
		}
		selectFeature(feature, { id: "__draw" }, label, idField);
	}

	function editGeomHandler() {
		if (!selected.layer)
			return;
		selected.feature.id = selected.layer.id + "|" + generateID();
		var ids = drawControl.add(selected.feature);
		var feature = selected.feature;
		if (feature.geometry.type === "LineString") {
			var geometry = helpers.lineString(feature.geometry.coordinates);
			var bounds = bbox(geometry);
			map.fitBounds(bounds, {
					padding: 100
			});
		}
		else if (feature.geometry.type === "Polygon") {
			var geometry = helpers.polygon(feature.geometry.coordinates);
			var bounds = bbox(geometry);
			map.fitBounds(bounds, {
					padding: 100
			});
		}
		else if (feature.geometry.type === "MultiLineString") {
			var geometry = helpers.multiLineString(feature.geometry.coordinates);
			var bounds = bbox(geometry);
			map.fitBounds(bounds, {
					padding: 100
			});
		}
		else if (feature.geometry.type === "MultiPolygon") {
			var geometry = helpers.multiPolygon(feature.geometry.coordinates);
			var bounds = bbox(geometry);
			map.fitBounds(bounds, {
					padding: 100
			});
		}
		else if (feature.geometry.type === "MultiPoint") {
			var geometry = helpers.multiPoint(feature.geometry.coordinates);
			var bounds = bbox(geometry);
			map.fitBounds(bounds, {
					padding: 100
			});
		}
		if (selected.feature.geometry.type == "Point") {
			editFeature = ids[0]
			drawControl.changeMode("simple_select", { featureIds: ids });
		}
		else
			drawControl.changeMode("direct_select", { featureId: ids[0] });
		var feature = selected.feature;
		var label = "User Feature";
		var idField = "_id";
		var labelField = "";

		var layer = selected.layer;
		if (layer.id == "reportlog" || layer.id == "reportlog_line" || layer.id == "reportlog_fill") {
			label = "Log Feature";
			idField = "history_id";
		}
		else {
			if (selected.layer.metadata && selected.layer.metadata["geosync:id"])
				idField = selected.layer.metadata["geosync:id"];
			if (selected.layer.metadata && selected.layer.metadata["geosync:label"]) {
				labelField = selected.layer.metadata["geosync:label"];
				if (selected.feature && selected.feature.properties[labelField])
					label = selected.feature.properties[labelField];
			}
		}
		selectFeature(feature, { id: "__draw" }, label, idField);
	}

	async function shareHandler(e) {
		e.currentTarget.blur();
		if (!selected.layer.metadata)
			return;
		var id = selected.feature.properties[selected.layer.metadata["geosync:id"]] + "";
		var media = selected.layer.metadata["geosync:media"] + "";
		let [res, err]  = await fetchData("/v1/share/feature", {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + session.token
            },
            body: JSON.stringify({
				media: media,
				id: id,
				feature: selected.feature
			})
    });
		if (err) {
			console.log(err);
			return;
		}
		if (res.status == "success") {
			var win = window.open(res.data.link, '_blank');
			win.focus();
		}
	}
	async function deleteHandler() {
		var metadata = {};
		metadata.user = session.name;
		metadata.user_id = session.id;
		metadata.created = new Date().toISOString();
		metadata.id = generateID();
		metadata.action = "delete";
		metadata.map = config.style.name;
		metadata.map_id = mapId;
		var selectedFeature = selected.feature;
		var selectedLayer = selected.layer;
		if (!selectedFeature || !selectedLayer)
			return;

		if (selectedLayer.id == "__user" || selectedLayer.id == "__draw" || selectedLayer.id == "reportlog")
			return;

		var idField = selectedLayer.metadata["geosync:id"];
		if (!selectedFeature.properties[idField])
			return;

		metadata.feature = selectedFeature.properties[idField];
		metadata.feature = metadata.feature + "";
		metadata.source = selectedLayer.source + "";
		metadata.dynamic = "0";
		if (selectedLayer.metadata["geosync:dynamic"])
			metadata.dynamic = "1";

		let [res, err]  = await fetchData("/v1/reports", {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + session.token
            },
            body: JSON.stringify({
				metadata: metadata,
				feature: {}
			})
        });
		if (err) {
			console.log(err);
			return;
		}
		if (res.status == "success") {
			createToast({
				message: "Delete pending"
			});
		}
	}
	function toggleDropdown(event) {
        if (event.currentTarget.nextSibling.nextSibling.style.display == "") {
            tooltips = false;
            event.currentTarget.nextSibling.nextSibling.style.display = "block";
            setTimeout(function () {
                tooltips = true;
            }, 150);
        }
        else {
            event.currentTarget.nextSibling.nextSibling.style.display = "";
            event.currentTarget.blur();
        }
    }
    function focusHandler(event) {
        setTimeout(function () {
            event.target.nextSibling.nextSibling.style.display = "";
        }, 500);
	}
	function closeDropdown(event) {
		event.target.parentNode.parentNode.style.display = ""
	}
	function centerGPS(e) {
		e.currentTarget.blur();
		map.flyTo({center: [location.longitude, location.latitude], zoom: 16});
	}
	class LabelButton {
		constructor(contents) {
			this._map = null; // mapbox-gl Map
			this._contents = contents || "";
			this._container = null; // HTMLelement
		}
    onAdd(map) {
			this._map = map;
      this._container = document.createElement("div");
      this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group p-2";
      this._container.innerHTML = this._contents;
      this._container.addEventListener("contextmenu", (e) => e.preventDefault());

      return this._container;
    }
		onRemove(map) {
			var node = this._container;
			if (node.parentNode) {
				node.parentNode.removeChild(node);
			}
			this._map = null;
		};
  }
</script>

<div class="app off-canvas {isOpen.sidebar ? "off-canvas-sidebar-show" : ""}">
	{#if modal}
		<svelte:component this={modalViews[modal.view]} isPublic={isPublic} role={config.role} modal={modal} page={page} pageId={mapId} settings={settings} session={session} tooltips={tooltips} messageToken={messageToken} on:message={messageHandler} />
	{/if}
	<Identity key={keys.toast}>
		{#if toasts.length > 0}
			<div id="toast">
				{#each toasts as toast}
					<div class="toast toast-primary" transition:fly="{{ y: 100, duration: 500 }}">
						<div class="tile tile-centered">
							{#if toast.sticky}
								<div class="tile-icon">
									<div class="loading d-inline-block pl-2 pr-2 float-left"></div>
								</div>
							{/if}
							<div class="tile-content">
								{toast.message}
							</div>
							<div class="tile-action">
								<button class="btn btn-clear float-right" on:click={(e) => {
									e.currentTarget.blur();
									closeToast(toast.id);
								}}></button>
							</div>
						</div>
					</div>
				{/each}
			</div>
		{/if}
	</Identity>
	<div id="sidebar" class="off-canvas-sidebar {isOpen.sidebar ? "active" : ""}">
		<div id="toggle-container">
			<button id="toggle" on:click|stopPropagation={(e) => {
					e.currentTarget.blur();
					isOpen.sidebar = !isOpen.sidebar;
				}} class="btn btn-primary ">
				<i class="icon {isOpen.sidebar ? "icon-arrow-left" : "icon-arrow-right"}"></i>	
			</button>
		</div>
		<Identity key={session.account}>
			<Tree tooltips={tooltips} isDrawing={isDrawing} isMeasuring={isMeasuring} selectedLayer={selected.layer} selectedFeature={selected.feature} vitals={vitals} settings={settings} isPublic={isPublic} keys={keys} links={links} mapId={mapId} history={history} messages={messages} location={location} layers={layers} interactiveLayers={interactiveLayers} mapLoaded={mapLoaded} map={map} tab={tab} groups={groups} config={config} workspace={workspace} projects={projects} session={session} on:message={messageHandler} />
		</Identity>
	</div>
	<div class="off-canvas-content">
		{#if map}
			<Identity key={keys.content}>
				<div class="navbar">
					<div class="navbar-section">
						<figure class="status avatar avatar-md {tooltips ? "tooltip tooltip-right" : ""} {location.enabled ? "active" : ""}"
							data-tooltip="Location: {location.label}" on:click={locationHandler}>
							<i class="icon icon-location"></i>
							<i class="avatar-presence {location.status}"></i>
						</figure>
						{#if location.enabled}
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""}" data-tooltip="Re-center GPS" on:click={centerGPS}><i class="svg">{@html locate.md}</i></button>
						{/if}
						{#if !isDrawing && !isMeasuring && forms.length > 0 && selected.feature}
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""}" data-tooltip="New Report" on:click={reportHandler}><i class="svg">{@html today.md}</i></button>
						{/if}
						{#if config.role >= 2 && !isDrawing && !isMeasuring && forms.length > 0 && location.enabled}
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""}" data-tooltip="New GeoReport" on:click={geoReportHandler}><i class="svg">{@html compass.ios}</i></button>
						{/if}
						{#if config.role >= 2 && !isDrawing && !isMeasuring && forms.length > 0 && selected.layer && selected.layer.id == "reportlog"}
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""}" data-tooltip="Edit Report" on:click={editHandler}><i class="svg">{@html create.md}</i></button>
						{/if}
						{#if config.role > 1 && !isDrawing && !isMeasuring && selected.feature && selected.feature.properties._id != "user"}
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""}" data-tooltip="Attach Media" on:click={attachHandler}><i class="svg">{@html attach.md}</i></button>
						{/if}
						{#if selected.feature && selected.feature.geometry.type == "LineString"}
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""}" data-tooltip="Profile" on:click={profileHandler}><i class="svg">{@html podium.md}</i></button>
						{/if}
						<button class="d-block btn {drawTools ? "btn-primary" : "btn-link"} {tooltips ? "tooltip tooltip-right" : ""}" data-tooltip="Draw Mode" on:click|stopPropagation={(e) => {
									e.currentTarget.blur();
									drawTools = !drawTools;
									if (!drawTools) {
										if (isDrawing) {
											map.getCanvas().style.cursor = "";
											var features = drawControl.getAll().features;
											var featureId = features[features.length - 1].id;
											drawControl.changeMode("simple_select", { featureIds: [featureId] });
											isDrawing = false;
										}
										else if (isMeasuring) {
											var empty = {
												"type": "FeatureCollection",
												"features": []
											}
											measurements = {
												"type": "FeatureCollection",
												"features": []
											};
											linestring = {
												"type": "Feature",
												"properties": {
													"_id": "user"
												},
												"geometry": {
													"type": "LineString",
													"coordinates": []
												}
											};
											map.getSource("measurements").setData(empty);
											map.off("click", measure.click);
											map.off("mousemove", measure.move);
											map.off("touchstart", measure.start);
											map.off("touchend", measure.end);
											map.getCanvas().style.cursor = "";
											deselectFeature();
											isMeasuring = false;
										}
										drawControl.deleteAll();
										deselectFeature();
									}
									else {
										drawControl.deleteAll();
										snapFeatures.map(function (feature) {
											var ids = drawControl.add(feature);
											snapIds = snapIds.concat(ids);
										})
									}
								}}>
							<i class="svg">{@html brush.md}</i>
						</button>
						{#if !drawTools && selected.layer && selected.layer.id != "__draw"}
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""} d-block" data-tooltip="Add to Snap Layer" on:click={snapHandler}><i class="svg">{@html colorWand.md}</i></button>
						{/if}
						{#if !drawTools && selected.layer && selected.layer.id != "__draw" && selected.layer.id != "scenes"}
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""}" data-tooltip="Share Feature" on:click={shareHandler}><i class="svg">{@html share.md}</i></button>
						{/if}
						{#if !drawTools && selected.feature && selected.feature.properties && selected.layer && selected.layer.id != "__draw"}
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""}" data-tooltip="Deselect Feature" on:click={deselectHandler}><i class="svg">{@html eyeOff.md}</i></button>				
						{/if}
						{#if !drawTools && selected.feature && selected.feature.properties && selected.layer && selected.layer.id != "__draw"}
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""}" data-tooltip="Feature Media" on:click|stopPropagation={(e) => {
											e.currentTarget.blur();
											isOpen.callout = true;
											details = "media";
										}}><i class="svg">{@html image.md}</i></button>				
						{/if}
						{#if bearing != 0}
							<Identity key={keys.rotate}>
								<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""}" data-tooltip="Reset Rotation" on:click|stopPropagation={(e) => {
											e.currentTarget.blur();
											map.setBearing(0);
										}}><i class="svg" style={"transform: rotate(" + (-1 * bearing) + "deg);"}>{@html navigate.md}</i></button>
							</Identity>
						{/if}
						<!--{#if !isMeasuring && selected.layer && (selected.layer.id == "__draw" || selected.layer.id == "__user")}
							<button class="btn btn-link tooltip tooltip-right" data-tooltip="Remove Feature" on:click={removeHandler}><i class="svg">{@html closeCircleOutline.md}</i></button>				
						{/if}-->
					</div>
					<div class="navbar-section">
						<div class="navbar-brand slant slant-left-reverse hide-xs"><div class="pl-1 pr-2" style="z-index:1">{config.style.name}</div></div>
					</div>
				</div>
				<!--<div id="toolbar" class="show-xs">
					{#if !isMeasuring && !isDrawing}
							<figure class="d-block mt-1 status avatar avatar-sm tooltip tooltip-right {location.enabled ? "active" : ""}"
								data-tooltip="Location: {location.label}" on:click={locationHandler}>
								<i class="icon icon-location"></i>
								<i class="avatar-presence {location.status}"></i>
							</figure>
						{/if}
						{#if !isMeasuring && !isDrawing && !selected.feature}
							<Identity key={keys.rotate}>
								<button class="d-block btn btn-link tooltip tooltip-right" data-tooltip="Reset Rotation" on:click|stopPropagation={(e) => {
											e.currentTarget.blur();
											map.setBearing(0);
										}}><i class="svg svg-light" style={"transform: rotate(" + (-1 * bearing) + "deg);"}>{@html navigate.md}</i></button>
							</Identity>		
						{/if}
						{#if config.role > 1 && !isDrawing && !isMeasuring && forms.length > 0 && selected.feature}
							<button class="d-block btn btn-link tooltip tooltip-right" data-tooltip="New Report" on:click={reportHandler}><i class="svg svg-light">{@html today.md}</i></button>
						{/if}
						{#if config.role > 1 && !isDrawing && !isMeasuring && forms.length > 0 && location.enabled}
							<button class="d-block btn btn-link tooltip tooltip-right" data-tooltip="New GeoReport" on:click={geoReportHandler}><i class="svg svg-light">{@html compass.ios}</i></button>
						{/if}
						{#if config.role > 1 && !isDrawing && !isMeasuring && forms.length > 0 && selected.layer && selected.layer.id == "reportlog"}
							<button class="d-block btn btn-link tooltip tooltip-right" data-tooltip="Edit Report" on:click={editHandler}><i class="svg svg-light">{@html create.md}</i></button>
						{/if}
						{#if !isDrawing && !isMeasuring && selected.feature && selected.feature.properties._id != "user"}
							<button class="d-block btn btn-link tooltip tooltip-right" data-tooltip="Attach Media" on:click={attachHandler}><i class="svg svg-light">{@html attach.md}</i></button>
						{/if}
						<button class="d-block btn btn-link tooltip tooltip-right" data-tooltip="Show Draw Tools" on:click|stopPropagation={(e) => {
									e.currentTarget.blur();
									drawTools = !drawTools;
								}}>
							<i class="svg svg-light">{@html brush.md}</i>
						</button>
						{#if !isDrawing && !isMeasuring && selected.layer && selected.layer.id != "__draw" && selected.layer.id != "scenes"}
							<button class="d-block btn btn-link tooltip tooltip-right" data-tooltip="Share Feature" on:click={shareHandler}><i class="svg svg-light">{@html share.md}</i></button>
						{/if}
						{#if selected.feature && selected.feature.properties && selected.layer && selected.layer.id != "__draw"}
							<button class="d-block btn btn-link tooltip tooltip-right" data-tooltip="Feature Media" on:click|stopPropagation={(e) => {
											e.currentTarget.blur();
											isOpen.callout = true;
											details = "media";
										}}><i class="svg svg-light">{@html image.md}</i></button>				
						{/if}
						{#if selected.feature && selected.feature.properties && selected.layer && selected.layer.id != "__draw" && selected.layer.id != "__user"}
							<button class="d-block btn btn-link tooltip tooltip-right" data-tooltip="Deselect Feature" on:click={deselectHandler}><i class="svg svg-light">{@html eyeOff.md}</i></button>				
						{/if}
						{#if !isMeasuring && selected.layer && (selected.layer.id == "__draw" || selected.layer.id == "__user")}
							<button class="d-block btn btn-link tooltip tooltip-right" data-tooltip="Remove Feature" on:click={removeHandler}><i class="svg svg-light">{@html closeCircleOutline.md}</i></button>				
						{/if}
				</div>-->
				{#if selected.feature && selected.layer}
					<div id="feature" class="slant slant-left">
						<div class="tile tile-centered">
							<div class="tile-content">
								<div class="tile-title">{selected.label}</div>
							</div>
							<div class="tile-action">
								<button class="btn btn-link tooltip tooltip-left" data-tooltip="Details" on:click|stopPropagation={(e) => {
									e.currentTarget.blur();
									isOpen.callout = !isOpen.callout;
								}}><i class="icon icon-arrow-down mt-1"></i></button>
							</div>
						</div>
					</div>
				{/if}
				<div id="controls" class="slant slant-right" style={isOpen.sidebar && vw > 480 ? "left:300px;": ""}>
					<div class="tile tile-centered" style="width: 110px;">
						<div class="tile-icon">
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""}" data-tooltip="Zoom In" on:click|stopPropagation={(e) => {
								e.currentTarget.blur();
								map.zoomIn()
							}}><i class="icon icon-plus mt-1"></i></button>
						</div>
						<div class="tile-content text-center">
							<div class="tile-title">{zoom}</div>
						</div>
						<div class="tile-action">
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""}" data-tooltip="Zoom Out" on:click|stopPropagation={(e) => {
								e.currentTarget.blur();
								map.zoomOut()
							}}><i class="icon icon-minus mt-1"></i></button>
						</div>
					</div>
				</div>
				{#if drawTools}
					<div id="draw-controls" class="slant" style={isOpen.sidebar && vw > 480 ? "left:300px;": ""} in:fade>
						{#if !isMeasuring && !isDrawing}
							<button class="btn btn-primary {tooltips ? "tooltip tooltip-right" : ""} d-block" data-tooltip="Start Measure" on:click|stopPropagation={(e) => {
											//closeDropdown(e);
											e.currentTarget.blur();
											measureHandler();
										}}><i class="svg svg-light">{@html swap.md}</i></button>
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""} d-block" data-tooltip="Draw Point" on:click|stopPropagation={(e) => {
										e.currentTarget.blur();
										drawHandler("draw_point");
									}}><i class="svg svg-light">{@html radioButtonOn.md}</i></button>
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""} d-block" data-tooltip="Draw Polyline" on:click|stopPropagation={(e) => {
										e.currentTarget.blur();
										drawHandler("draw_line_string");
									}}><i class="svg svg-light">{@html analytics.md}</i></button>
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""} d-block" data-tooltip="Draw Polygon" on:click|stopPropagation={(e) => {
										e.currentTarget.blur();
										drawHandler("draw_polygon");
									}}><i class="svg svg-light">{@html squareOutline.md}</i></button>
						{/if}
						{#if !isMeasuring && isDrawing}
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""} d-block" data-tooltip="End Draw" on:click|stopPropagation={(e) => {
								e.currentTarget.blur();
								drawHandler();
							}}><i class="svg svg-light">{@html checkmark.md}</i></button>
						{/if}
						{#if !isDrawing && isMeasuring}
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""} d-block" data-tooltip="End Measure" on:click|stopPropagation={(e) => {
								e.currentTarget.blur();
								measureHandler();
							}}><i class="svg svg-light">{@html checkmark.md}</i></button>
						{/if}
						{#if config.role > 2 && !isDrawing && !isMeasuring && selected.layer && selected.layer.id != "__draw" && selected.layer.id != "scenes"}
							<button class="d-block btn btn-link {tooltips ? "tooltip tooltip-right" : ""} d-block" data-tooltip="Edit Geometry" on:click|stopPropagation={(e) => {
								e.currentTarget.blur();
								editGeomHandler();
							}}><i class="svg svg-light">{@html move.md}</i></button>
							<button class="d-block btn btn-link {tooltips ? "tooltip tooltip-right" : ""} d-block" data-tooltip="Delete Feature" on:click|stopPropagation={(e) => {
										e.currentTarget.blur();
										deleteHandler();
							}}><i class="svg svg-light">{@html trash.md}</i></button>
						{/if}
						{#if config.role > 2 && !isDrawing && !isMeasuring && selected.layer && selected.layer.id != "__draw" && selected.layer.id != "scenes" && location.enabled && selected.feature && selected.feature.geometry.type == "Point"}
							<button class="d-block btn btn-link {tooltips ? "tooltip tooltip-right" : ""} d-block" data-tooltip="Move Feature to Current Location" on:click|stopPropagation={(e) => {
										e.currentTarget.blur();
										editGeomLocationHandler();
							}}><i class="svg svg-light">{@html locate.md}</i></button>
						{/if}
						{#if !isMeasuring && !isDrawing}
							<button class="btn btn-link {tooltips ? "tooltip tooltip-right" : ""} d-block" data-tooltip="Clear Snap Layer" on:click|stopPropagation={(e) => {
										e.currentTarget.blur();
										drawControl.delete(snapIds);
										snapFeatures = [];
										snapIds = [];
									}}><i class="svg svg-light">{@html sync.md}</i></button>
						{/if}
					</div>
				{/if}
			</Identity>
		{/if}
		{#if isOpen.callout}
			<Identity key={keys.callout}>
				<Callout isPublic={isPublic} keys={keys} tab={details} selected={selected} config={config} session={session}  on:message={messageHandler} />
			</Identity>
		{/if}
		<div id="map" />
	</div>
</div>

<style>
	#toggle-container {
		position: fixed;
		width: 340px;
	}
	#toggle {
		position: absolute;
		right: 0;
		top: 0.2rem;
		width: 40px;
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
	}
	#feature {
		position: fixed;
		right: 0;
		top: 2.2rem;
		width: 300px;
		padding: 0 0 0 0.4rem;
		z-index: 2;
		box-shadow: 0.1rem 0.2rem 0.4rem rgba(0,0,0,.3);
	}
	.slant {
		color: white;
		background-color: #00ab5f;
		transform-origin: bottom left;
		position: relative;
		z-index: 1;
	}
	.slant-right:before {
		content: "";
		position: absolute;
		z-index: -1;
		top: 0;
		bottom: 0;
		right: 0;
		width: 20px;
		margin-right: -10px;
		background-color: #00ab5f;
		border-right: 2px solid #a9d9bb;
		-ms-transform: skew(-30deg, 0deg);
		-webkit-transform: skew(-30deg, 0deg);
		transform: skew(-30deg, 0deg);
	}
	.slant-left:before {
		content: "";
		position: absolute;
		z-index: -1;
		top: 0;
		bottom: 0;
		left: 0;
		width: 20px;
		margin-left: -10px;
		background-color: #00ab5f;
		border-left: 2px solid #a9d9bb;
		-ms-transform: skew(30deg, 0deg);
		-webkit-transform: skew(30deg, 0deg);
		transform: skew(30deg, 0deg);
	}
	.slant-left-reverse:before {
		content: "";
		position: absolute;
		z-index: -1;
		top: 0;
		bottom: 0;
		left: 0;
		width: 20px;
		margin-left: -10px;
		background-color: #00ab5f;
		border-left: 2px solid #a9d9bb;
		-ms-transform: skew(-30deg, 0deg);
		-webkit-transform: skew(-30deg, 0deg);
		transform: skew(-30deg, 0deg);
	}
	#controls {
		position: fixed;
		left: 0;
		top: 2.2rem;
		padding-left: 0.2rem;
		box-shadow: -0.1rem 0.2rem 0.4rem rgba(0,0,0,.3);
	}
	#draw-controls {
		background-color: #00ab5f;
		position: fixed;
		left: 0;
		top: 4.2rem;
		box-shadow: -0.1rem 0.2rem 0.4rem rgba(0,0,0,.3);
	}
	.slant .btn.btn-link {
		color: white;
	}
	.slant .btn.btn-link:hover  {
		color: #ccc;
	}
	.app {
		height: 100vh; /* Fallback for browsers that do not support Custom Properties */
		height: calc(var(--vh, 1vh) * 100);
		overflow: hidden;
	}
	.off-canvas-sidebar {
		width: 300px;
	}
	#toast {
		position: fixed;
		right: 1rem;
		bottom: 1rem;
		width: 300px;
		z-index: 9999;
	}
	.toast {
		margin-top: 0.2rem;
	}
	#sidebar {
		overflow: visible;
		box-shadow: 0 2.2rem 1rem rgba(0,0,0,.3), 0 2.2rem 12px rgba(0,0,0,.22);
	}
	.off-canvas .off-canvas-content {
		padding: 0;
		padding-bottom: 0.4rem;
		overflow: hidden;
		z-index: 1;
	}
	.loading::after {
		border: 0.1rem solid white;
		border-right-color: transparent;
    	border-top-color: transparent;
	}
  #map {
		z-index: -1;
    bottom: 0;
		left: 0;
		margin: 0;
		overflow: hidden !important;
		position: fixed;
		right: 0;
		top: 2.2rem;
		-webkit-touch-callout: none !important;
  }
  .navbar {
		position: fixed;
		width: 100%;
		overflow: hidden;
    background: #efefef;
    padding: 0.2rem 0 0.2rem 2rem;
		box-shadow: 0 0.2rem 0.4rem rgba(0,0,0,.3);
	}
	.btn.btn-link.active {
        background-color: #ccc;
    }
	.dropdown-container.four-buttons {
			width: 8.4rem;
			left: 2rem;
			z-index: 2;
    }
    .no-hover {
        pointer-events: none;
    }
	.status {
		text-align: center;
		padding: .2rem 0;
		margin: 0 0.4rem;
		cursor: pointer;
		color: #00ab5f;
		background-color: #efefef;
		border: 1px solid #00ab5f;
	}
	.status.active {
		background-color: rgb(221, 209, 209);
	}
	@media screen and (max-width: 400px) {
		#controls {
			width: calc(100% - 230px);
		}
		#feature {
			width: 230px;
		}
	}
</style>